import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../services/api";
import { AUTORISATIONS_LECTEUR, urlLecteurYoutube } from "../services/lecteurYoutube";
import ProfileModal from "./ProfileModal";
import MessagesView from "./MessagesView";
import AssistanceEnseignant from "./assistance/AssistanceEnseignant";
import NotifBell from "./NotifBell";
import { BoutonParametres } from "./Parametres";
import SupervisionDashboard, { CAMEROON_MAP_TILE_URL, CAMEROON_MAP_TILE_OPTIONS } from "./SupervisionDashboard";
import AdminSupportSpace from "./AdminSupportSpace";
import AdminReferralSpace from "./AdminReferralSpace";
import { ComptaDashboardPage, ComptaDepensesPage, ComptaRevenusPage, ComptaSalairesPage } from "./compta/ComptaEspace";
import {
  CMCalendrierPage, CMCommunautePage, CMContenusPage, CMDashboardPage, CMRessourcesPage, CMStatistiquesPage,
} from "./community/CommunityEspace";
import {
  SECAnnuairePage, SECComptePage, SECDashboardPage, SECManuelsPage, SECRegistrePage,
} from "./secretariat/SecretariatEspace";
import {
  AMBDashboardPage, AMBNouvelleVentePage, AMBPerformancesPage, AMBRecouvrementsPage, AMBReversementsPage, AMBStockPage,
  AMBVentesPage, AMBVersementsPage, GestionAmbassadeursPage,
} from "./ambassadeur/AmbassadeurEspace";
import StudentRankingAdmin from "./StudentRankingAdmin";
import MathText, { MathDisplay } from "./MathText";
import { qlAlert, qlConfirm } from "../utils/dialogs";
import DownloadLink from "./DownloadLink";
import EnseignantDevoirs from "./EnseignantDevoirs";
import downloadFile from "../services/downloadFile";
import { imprimerDocument } from "../services/imprimer";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Tooltip, Legend, Filler,
} from "chart.js";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { Pastille, rafraichirCompteurs, useCompteurs, useNotificationsDeLaRubrique } from "../services/compteurs";
import { espaceUtilisateur, roleUtilisateur } from "../services/roles";
import VisiteGuidee, { visiteDejaVue } from "./VisiteGuidee";
import L from "leaflet";
import BannierePublicitaire from "./BannierePublicitaire";
import MesContenus from "./MesContenus";

// Page de l'interface → rubriques de notifications qu'elle « ouvre ».
const RUBRIQUES_ADMIN = {
  messages: ["messages"], assistances: ["assistances"], boite_signature: ["boite"], groupes: ["groupes"],
  compta_depenses: ["compta_depenses"], compta_revenus: ["compta_revenus"], users: ["users"],
  enseignants: ["enseignants", "users"], supervision: ["supervision"], soutien_scolaire: ["soutien_scolaire"],
  rapports: ["rapports"], cm_communaute: ["cm_communaute"], cm_contenus: ["cm_contenus"], cm_ressources: ["cm_ressources"],
  sec_demandes: ["cm_communaute"],
};
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Tooltip, Legend, Filler);

// ── Helpers ────────────────────────────────────────

// Ouvre un blob PDF : téléchargement sur desktop, nouvel onglet sur mobile
// (iOS/Android ne supportent pas a.download ni iframe.print())
function pdfOpen(blob, filename) {
  return downloadFile({
    url: `${api.defaults.baseURL}generated/${encodeURIComponent(filename)}`,
    filename,
    mimeType: "application/pdf",
    data: blob,
  }).then(result => {
    if (!result?.cancelled) qlAlert("PDF téléchargé avec succès.", "success");
    return result;
  }).catch(() => qlAlert("Téléchargement du PDF impossible.", "error"));
}

// Document que l'on vient de produire : téléchargé, puis proposé à l'impression.
function telechargerPuisProposerImpression(blob, filename, libelle) {
  pdfOpen(blob, filename).finally(() => qlConfirm(`Imprimer ${libelle} maintenant ?`, () => pdfPrint(blob), { okLabel: "Imprimer" }));
}

function pdfPrint(blob) {
  // Impression commune : navigateur, téléphone et applications (visionneuse du système).
  return imprimerDocument({ data: blob, filename: "document.pdf" }).catch(() => qlAlert("Impression impossible.", "error"));
}

const ROLE_LABELS = { admin: "Administrateur", enseignant: "Enseignant", eleve: "Élève", comptable: "Comptable", generateur: "Générateur", parent: "Parent" };
const ROLE_COLORS = { admin: "#f5820d", enseignant: "#198754", eleve: "#0d6efd", comptable: "#7c3aed", generateur: "#0891b2", parent: "#d63384" };

function roleBadge(role, libelle) {
  return (
    <span className="ap-role-badge" style={{ background: ROLE_COLORS[role] || "#64748b" }}>
      {libelle || ROLE_LABELS[role] || role}
    </span>
  );
}

function statusBadge(is_active) {
  return (
    <span className={`ap-status-badge${is_active ? " active" : " inactive"}`}>
      {is_active ? "Actif" : "Suspendu"}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

function formatDateShort(iso) {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function gradeColor(gradeStr) {
  if (!gradeStr) return "#64748b";
  const num = parseFloat(String(gradeStr).replace(",", ".").split("/")[0]);
  if (isNaN(num)) return "#64748b";
  if (num < 5)  return "#dc3545";
  if (num > 16) return "#0d6efd";
  return "#e6a817";
}

function avatarLetters(nom) {
  if (!nom) return "?";
  const parts = nom.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return nom.slice(0, 2).toUpperCase();
}

function PasswordReveal({ value }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div className="ap-detail-row-val" style={{ display:"flex", alignItems:"center", gap:6 }}>
      <span style={{ fontFamily:"monospace", letterSpacing: visible ? 0 : 2 }}>
        {visible ? value : "•".repeat(Math.min(value.length, 10))}
      </span>
      <button
        onClick={() => setVisible(v => !v)}
        style={{ background:"none", border:"none", cursor:"pointer", color:"#94a3b8", padding:"2px 4px", fontSize:"0.85rem" }}
        title={visible ? "Masquer" : "Afficher"}
      >
        <i className={`bi ${visible ? "bi-eye-slash" : "bi-eye"}`}></i>
      </button>
    </div>
  );
}

function getYouTubeId(codeOrUrl) {
  if (!codeOrUrl) return null;
  // ?v=ID (youtube.com/watch, mobile, partage)
  let m = codeOrUrl.match(/[?&]v=([^?&\s]+)/);
  if (m) return m[1];
  // youtu.be/ID | shorts/ID | embed/ID | v/ID (stop à ? et &)
  m = codeOrUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:shorts|embed|v)\/)([^?&\s]+)/);
  if (m) return m[1];
  // code field is already the bare ID if no URL match
  return codeOrUrl.length <= 20 ? codeOrUrl : null;
}

// Lecteur vidéo intégré (thumbnail → iframe au clic)
function APVideoCard({ video, playing, onPlay }) {
  const ytId = getYouTubeId(video.youtube_url) || getYouTubeId(video.code);

  return (
    <div className="ap-video-card">
      {playing ? (
        <>
          {!ytId ? (
            <div className="ap-video-error">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <p>Lien YouTube invalide.</p>
            </div>
          ) : (
            <div className="ap-video-embed">
              <iframe
                width="100%"
                height="450"
                src={urlLecteurYoutube(ytId)}
                title={video.title}
                frameBorder="0"
                allow={AUTORISATIONS_LECTEUR}
                allowFullScreen
              />
            </div>
          )}
        </>
      ) : (
        <div
          className="ap-video-thumb"
          onClick={onPlay}
          style={{ backgroundImage: ytId ? `url(https://img.youtube.com/vi/${ytId}/hqdefault.jpg)` : "none" }}
        >
          {!ytId && <i className="bi bi-camera-video ap-video-no-thumb"></i>}
          <div className="ap-video-play-btn"><i className="bi bi-play-circle-fill"></i></div>
        </div>
      )}
      <div className="ap-video-info">
        <div className="ap-video-title">{video.title}</div>
        {video.module_title && <div className="ap-video-sub"><i className="bi bi-layers me-1"></i>{video.module_title}</div>}
        {video.duration && <div className="ap-video-sub"><i className="bi bi-clock me-1"></i>{video.duration}</div>}
      </div>
    </div>
  );
}

// Visite guidée de l'espace (premier accès, puis bouton « ? ») : seuls les boutons
// affichés pour la fonction de la personne sont présentés.
const ETAPES_ADMIN = [
  { cible: '[data-visite="menu"]', icone: "bi-list", titre: "Le menu", texte: "Ouvre ou réduis le menu de ton espace." },
  { cible: '[data-visite="dashboard"]', icone: "bi-house-door", titre: "Tableau de bord", texte: "La vue d'ensemble de la plateforme." },
  { cible: '[data-visite="supervision"]', icone: "bi-activity", titre: "Centre temps réel", texte: "Qui est connecté, depuis quel appareil et où, en direct." },
  { cible: '[data-visite="lecons"]', icone: "bi-file-text", titre: "Le contenu pédagogique", texte: "Modules, leçons, exercices, devoirs, QCM, vidéos et manuels." },
  { cible: '[data-visite="devoirs"]', icone: "bi-journal-text", titre: "Les devoirs", texte: "Les devoirs avec leur corrigé et leur barème." },
  { cible: '[data-visite="users"]', icone: "bi-people", titre: "Les utilisateurs", texte: "Élèves, enseignants et comptes de la plateforme." },
  { cible: '[data-visite="codes_comptable"]', icone: "bi-person-badge", titre: "Les codes du personnel", texte: "Génère ici les codes d'inscription du comptable et de la Community Manager." },
  { cible: '[data-visite="compta_dashboard"]', icone: "bi-speedometer2", titre: "La comptabilité", texte: "Revenus, salaires et dépenses, avec leur circuit d'approbation." },
  { cible: '[data-visite="cm_dashboard"]', icone: "bi-megaphone", titre: "La communication", texte: "Calendrier éditorial, contenus à faire valider, demandes et statistiques." },
  { cible: '[data-visite="boite_signature"]', icone: "bi-inbox", titre: "La boîte de dépôt", texte: "Documents à signer et communiqués ; un point rouge signale un document pas encore ouvert." },
  { cible: '[data-visite="messages"]', icone: "bi-chat-dots", titre: "La messagerie", texte: "Tes conversations ; les validations (dépenses, contenus) arrivent aussi ici." },
  { cible: '[data-visite="assistances"]', icone: "bi-question-circle", titre: "Les assistances", texte: "Les élèves qui demandent de l'aide sur un exercice." },
  { cible: '[data-visite="notifications"]', icone: "bi-bell", titre: "Les notifications", texte: "Le point rouge disparaît quand tu ouvres la rubrique concernée." },
  { cible: '[data-visite="aide"]', icone: "bi-question-circle", titre: "Revoir la visite", texte: "Touche ce bouton pour relancer cette présentation à tout moment." },
  { cible: '[data-visite="profil"]', icone: "bi-person-circle", titre: "Ton profil", texte: "Ta photo, ta signature et tes informations." },
];

// ── Sidebar ────────────────────────────────────────
const NAV = [
  { section: "TABLEAU DE BORD", items: [{ icon: "bi-house-door", label: "Tableau de bord", page: "dashboard" }] },
  { section: "SUPERVISION", superAdminOnly: true, items: [
    { icon: "bi-activity", label: "Centre temps réel", page: "supervision" },
    { icon: "bi-envelope-check", label: "Diagnostic e-mail", page: "diagnostic_email" },
  ] },
  {
    section: "CONTENU PÉDAGOGIQUE", items: [
      { icon: "bi-plus-square", label: "Ajouter des contenus", page: "mes_contenus" },
      { icon: "bi-layers", label: "Modules", page: "modules" },
      { icon: "bi-file-text", label: "Leçons", page: "lecons" },
      { icon: "bi-pencil-square", label: "Exercices", page: "exercices" },
      { icon: "bi-journal-text", label: "Devoirs", page: "devoirs" },
      { icon: "bi-grid-3x3", label: "QCM", page: "qcm" },
      { icon: "bi-play-circle", label: "Vidéos", page: "videos" },
      { icon: "bi-book", label: "Manuels", page: "manuels" },
      { icon: "bi-infinity", label: "Formules utiles", page: "formules" },
      { icon: "bi-broadcast", label: "Quick Lives", page: "lives_admin" },
    ],
  },
  {
    section: "GESTION DES UTILISATEURS", items: [
      { icon: "bi-people", label: "Utilisateurs", page: "users" },
      { icon: "bi-person-badge", label: "Enseignants", page: "enseignants" },
      { icon: "bi-diagram-3", label: "Groupes", page: "groupes" },
    ],
  },
  {
    section: "ADMINISTRATION", superAdminOnly: true, items: [
      { icon: "bi-key",          label: "Codes Manuels",    page: "codes" },
      { icon: "bi-person-badge", label: "Codes Comptables", page: "codes_comptable", primaryOnly: true },
      { icon: "bi-megaphone",    label: "Codes Community",  page: "codes_community", primaryOnly: true },
      { icon: "bi-person-workspace", label: "Codes Secrétariat", page: "codes_secretariat", primaryOnly: true },
      { icon: "bi-shop",         label: "Codes Ambassadeurs", page: "codes_ambassadeur" },
      { icon: "bi-tag",          label: "Prix des manuels", page: "prix_manuels",    primaryOnly: true },
      { icon: "bi-shield-lock",  label: "Journal d'Audit",  page: "audit",           primaryOnly: true },
      { icon: "bi-file-earmark-check", label: "Documents à valider", page: "sec_documents", primaryOnly: true },
    ],
  },
  {
    section: "SOUTIEN SCOLAIRE", superAdminOnly: true, items: [
      { icon: "bi-mortarboard", label: "Soutien scolaire", page: "soutien_scolaire" },
      { icon: "bi-trophy", label: "Élèves à primer", page: "classement_eleves" },
      { icon: "bi-people-fill", label: "Parrainage", page: "referrals" },
    ],
  },
  {
    section: "STATISTIQUES", items: [
      { icon: "bi-graph-up", label: "Activité", page: "activite" },
      { icon: "bi-bar-chart", label: "Rapports", page: "rapports" },
    ],
  },
  {
    section: "COMPTABILITÉ", comptableOnly: true, primaryOnly: true, items: [
      { icon: "bi-speedometer2", label: "Tableau de bord", page: "compta_dashboard" },
      { icon: "bi-cash-coin",    label: "Revenus",          page: "compta_revenus"  },
      { icon: "bi-wallet2",      label: "Salaires",         page: "compta_salaires" },
      { icon: "bi-receipt",      label: "Dépenses",         page: "compta_depenses" },
    ],
  },
  {
    section: "COMMUNICATION", cmOnly: true, primaryOnly: true, items: [
      { icon: "bi-speedometer2",   label: "Tableau de bord",      page: "cm_dashboard" },
      { icon: "bi-calendar3",      label: "Calendrier éditorial", page: "cm_calendrier" },
      { icon: "bi-images",         label: "Contenus",             page: "cm_contenus" },
      { icon: "bi-chat-square-text", label: "Communauté",         page: "cm_communaute" },
      { icon: "bi-graph-up-arrow", label: "Statistiques",         page: "cm_statistiques" },
      { icon: "bi-folder2-open",   label: "Ressources de marque", page: "cm_ressources" },
    ],
  },
  {
    section: "SECRÉTARIAT", secOnly: true, items: [
      { icon: "bi-house-door",     label: "Tableau de bord",      page: "sec_dashboard" },
      { icon: "bi-inbox-fill",     label: "Demandes et suivi",    page: "sec_demandes" },
      { icon: "bi-calendar-event", label: "Agenda",               page: "sec_agenda" },
      { icon: "bi-check2-square",  label: "Mes tâches",           page: "sec_taches" },
      { icon: "bi-people",         label: "Parents",              page: "sec_parents" },
      { icon: "bi-person-video3",  label: "Enseignants",          page: "sec_enseignants" },
      { icon: "bi-mortarboard",    label: "Élèves",               page: "sec_eleves" },
      { icon: "bi-building",       label: "Établissements",       page: "sec_etablissements" },
      { icon: "bi-book",           label: "Manuels et commandes", page: "sec_manuels" },
      { icon: "bi-envelope-paper", label: "Courriers",            page: "sec_courriers" },
      { icon: "bi-file-earmark-text", label: "Documents",         page: "sec_documents" },
      { icon: "bi-gear",           label: "Mon compte",           page: "sec_compte" },
    ],
  },
  {
    section: "BRAND AMBASSADOR", ambOnly: true, items: [
      { icon: "bi-house-door",   label: "Tableau de bord",  page: "amb_dashboard" },
      { icon: "bi-bag-plus",     label: "Nouvelle vente",   page: "amb_vente" },
      { icon: "bi-journal-text", label: "Mes ventes",       page: "amb_ventes" },
      { icon: "bi-cash-coin",    label: "Versements",       page: "amb_versements" },
      { icon: "bi-box-seam",     label: "Stock de manuels", page: "amb_stock" },
      { icon: "bi-alarm",        label: "Recouvrements",    page: "amb_recouvrements" },
      { icon: "bi-bank",         label: "Reversements",     page: "amb_reversements" },
      { icon: "bi-graph-up-arrow", label: "Performances",   page: "amb_performances" },
    ],
  },
  {
    section: "VENTES DE MANUELS", gestionAmbassadeurs: true, items: [
      { icon: "bi-shop", label: "Brand Ambassadors", page: "ambassadeurs", comptableAllowed: true, cmAllowed: true, secAllowed: true },
    ],
  },
  {
    section: "SIGNATURE", items: [
      { icon: "bi-pen",          label: "Ma Signature",  page: "ma_signature",    comptableAllowed: true, cmAllowed: true, secAllowed: true, ambAllowed: true },
      { icon: "bi-inbox",        label: "Boîte de dépôt",page: "boite_signature", comptableAllowed: true, cmAllowed: true, secAllowed: true, ambAllowed: true, compteur: "boite" },
    ],
  },
  {
    section: "COMMUNIQUER", items: [
      { icon: "bi-chat-dots",        label: "Messages",    page: "messages",    comptableAllowed: true, cmAllowed: true, secAllowed: true, ambAllowed: true, compteur: "messages" },
      { icon: "bi-headset",          label: "Assistance",  page: "assistances", compteur: "assistances" },
    ],
  },
];

function Sidebar({ page, onPage, collapsed, isOpen, user, onProfileClick, compteurs = {} }) {
  return (
    <aside className={`ap-sidebar${collapsed ? " collapsed" : ""}${isOpen ? " ap-sidebar--open" : ""}`}>
      <div className="ap-sidebar-logo">
        <img src="/static/logo.png" alt="Quick Learning" className="ap-logo-img" />
        {!collapsed && (
          <div className="ap-logo-text">
            <span className="ap-logo-name">QUICK LEARNING</span>
            <span className="ap-logo-sub">{espaceUtilisateur(user)}</span>
          </div>
        )}
      </div>

      <nav className="ap-nav">
        {NAV.filter(s => {
          const isPureComptable = user?.is_comptable && !user?.is_superuser && !user?.is_staff;
          const isPureCM = user?.is_community_manager && !user?.is_superuser && !user?.is_staff;
          const isPureSec = user?.is_secretaire && !user?.is_superuser && !user?.is_staff;
          const isPureAmb = user?.is_brand_ambassador && !user?.is_superuser && !user?.is_staff;
          if (isPureAmb) return s.ambOnly === true || s.items.some(i => i.ambAllowed);
          if (isPureSec) return s.secOnly === true || s.items.some(i => i.secAllowed);
          if (isPureCM) return s.cmOnly === true || s.items.some(i => i.cmAllowed);
          if (isPureComptable) return s.comptableOnly === true || s.items.some(i => i.comptableAllowed);
          if (s.primaryOnly && !user?.is_primary_admin) return false;
          if (s.superAdminOnly && !user?.is_superuser) return false;
          // Les espaces Secrétariat et Brand Ambassador ne s'affichent que pour leurs titulaires :
          // le CEO suit leur activité dans le journal d'audit.
          if (s.secOnly && !user?.is_secretaire) return false;
          if (s.ambOnly && !user?.is_brand_ambassador) return false;
          if (s.gestionAmbassadeurs && !(user?.is_superuser || user?.is_comptable || user?.is_secretaire || user?.is_community_manager)) return false;
          if (s.comptableOnly && !user?.is_superuser && !user?.is_comptable) return false;
          return true;
        }).map((section, si) => (
          <div key={si} className="ap-nav-section">
            {!collapsed && <div className="ap-nav-section-title">{section.section}</div>}
            {section.items.filter(item => {
              if (item.primaryOnly && !user?.is_primary_admin) return false;
              const isPureComptable = user?.is_comptable && !user?.is_superuser && !user?.is_staff;
              if (isPureComptable && !section.comptableOnly && !item.comptableAllowed) return false;
              const isPureCM = user?.is_community_manager && !user?.is_superuser && !user?.is_staff;
              if (isPureCM && !section.cmOnly && !item.cmAllowed) return false;
              const isPureSec = user?.is_secretaire && !user?.is_superuser && !user?.is_staff;
              if (isPureSec && !section.secOnly && !item.secAllowed) return false;
              const isPureAmb = user?.is_brand_ambassador && !user?.is_superuser && !user?.is_staff;
              if (isPureAmb && !section.ambOnly && !item.ambAllowed) return false;
              return true;
            }).map(item => (
              <button
                key={item.page}
                data-visite={item.page}
                className={`ap-nav-item${page === item.page ? " active" : ""}`}
                onClick={() => onPage(item.page)}
                title={collapsed ? item.label : ""}
              >
                <i className={`bi ${item.icon}`}></i>
                {!collapsed && <span>{item.label}</span>}
                <Pastille nombre={item.compteur ? compteurs[item.compteur] : 0} />
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="ap-sidebar-footer">
        {!collapsed && (
          <div
            className="ap-sidebar-user"
            onClick={onProfileClick}
            title="Modifier mon profil"
            style={{ cursor: "pointer" }}
          >
            <div className="ap-sidebar-avatar">{avatarLetters(user?.nom_complet || user?.username)}</div>
            <div>
              <div className="ap-sidebar-uname">{user?.nom_complet || user?.username}</div>
              <div className="ap-sidebar-urole">
                {roleUtilisateur(user)}
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

// ── TopBar ─────────────────────────────────────────
function TopBar({ onToggle, onSearch, searchQ, user, onLogout, onProfileClick, nonLus = 0, onAide }) {
  return (
    <header className="ap-topbar">
      <button className="ap-topbar-toggle" data-visite="menu" onClick={onToggle}>
        <i className="bi bi-list"></i>
        <Pastille nombre={nonLus} className="ql-pastille--menu" />
      </button>
      <img src="/static/logo.png" alt="Quick Learning" className="ap-topbar-logo-mobile" />
      <div className="ap-topbar-search">
        <i className="bi bi-search ap-search-icon"></i>
        <input
          className="ap-search-input"
          placeholder={user?.is_brand_ambassador && !user?.is_superuser ? "Rechercher une vente (élève, école, code)…"
            : user?.is_secretaire && !user?.is_superuser ? "Rechercher un élève (nom, classe, école)…" : "Rechercher un utilisateur, un email…"}
          value={searchQ}
          onChange={e => onSearch(e.target.value)}
        />
      </div>
      <div className="ap-topbar-right">
        <span data-visite="notifications" className="d-inline-flex"><NotifBell variant="admin" /></span>
        <BoutonParametres className="ap-topbar-icon-btn" />
        <button className="ap-topbar-icon-btn ap-topbar-help" data-visite="aide" onClick={onAide} title="Aide — revoir la visite guidée">
          <i className="bi bi-question-circle"></i>
        </button>
        <div className="ap-topbar-user">
          <div
            className="ap-topbar-avatar"
            onClick={onProfileClick}
            title="Modifier mon profil"
            style={{ cursor: "pointer" }}
          >
            {user?.photo
              ? <img src={user.photo} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
              : avatarLetters(user?.nom_complet || user?.username)}
          </div>
          <div className="ap-topbar-userinfo" data-visite="profil" onClick={onProfileClick} style={{ cursor: "pointer" }}>
            <div className="ap-topbar-uname">{user?.nom_complet || user?.username}</div>
            <div className="ap-topbar-urole">
              {roleUtilisateur(user)}
            </div>
          </div>
          <button className="ap-topbar-logout" onClick={onLogout} title="Déconnexion">
            <i className="bi bi-box-arrow-right"></i>
          </button>
        </div>
      </div>
    </header>
  );
}

// ── StatCard ───────────────────────────────────────
function StatCard({ icon, color, value, label, sub }) {
  return (
    <div className="ap-stat-card">
      <div className="ap-stat-icon" style={{ background: color + "20", color }}>
        <i className={`bi ${icon}`}></i>
      </div>
      <div className="ap-stat-body">
        <div className="ap-stat-value">{value?.toLocaleString("fr-FR") ?? "–"}</div>
        <div className="ap-stat-label">{label}</div>
        {sub && <div className="ap-stat-sub">{sub}</div>}
      </div>
    </div>
  );
}

// ── Dashboard page ─────────────────────────────────
function DashboardPage({ stats, onPage }) {
  if (!stats) return <div className="ap-loading"><div className="spinner-border text-primary"></div></div>;
  const u = stats.users || {};
  const c = stats.content || {};
  const a = stats.activity || {};
  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <h1 className="ap-page-title">Tableau de bord</h1>
          <p className="ap-page-desc">Vue d'ensemble de la plateforme Quick Learning</p>
        </div>
      </div>

      <h2 className="ap-section-title">Utilisateurs</h2>
      <div className="ap-stats-grid">
        <StatCard icon="bi-people-fill"    color="#0d6efd" value={u.total}    label="Total utilisateurs" sub={`${u.active} actifs`} />
        <StatCard icon="bi-person-badge"   color="#198754" value={u.teachers} label="Enseignants" sub={`${u.eleves} élèves`} />
        <StatCard icon="bi-mortarboard"    color="#6f42c1" value={u.eleves}   label="Élèves" />
        <StatCard icon="bi-shield-lock"    color="#f5820d" value={u.admins}   label="Administrateurs" />
        <StatCard icon="bi-person-x"       color="#dc3545" value={u.inactive} label="Comptes inactifs" />
      </div>

      <h2 className="ap-section-title mt-4">Contenu pédagogique</h2>
      <div className="ap-stats-grid">
        <StatCard icon="bi-layers"         color="#0d6efd" value={c.modules}    label="Modules" />
        <StatCard icon="bi-file-text"      color="#6f42c1" value={c.lecons}     label="Leçons" />
        <StatCard icon="bi-play-circle"    color="#198754" value={c.videos}     label="Vidéos" />
        <StatCard icon="bi-pencil-square"  color="#f5820d" value={c.exercices}  label="Exercices" />
        <StatCard icon="bi-grid-3x3"       color="#20c997" value={c.qcms}       label="QCM" />
        <StatCard icon="bi-book"           color="#e83e8c" value={c.manuels}    label="Manuels" />
        <StatCard icon="bi-building"       color="#fd7e14" value={c.classes}    label="Classes" />
        <StatCard icon="bi-clipboard-check" color="#0dcaf0" value={c.evaluations} label="Évaluations" />
      </div>

      <h2 className="ap-section-title mt-4">Activité globale</h2>
      <div className="ap-stats-grid">
        <StatCard icon="bi-trophy"         color="#198754" value={a.qcm_attempts}      label="Tentatives QCM" />
        <StatCard icon="bi-pencil"         color="#6f42c1" value={a.exercice_attempts} label="Exercices réalisés" />
        <StatCard icon="bi-play-btn"       color="#f5820d" value={a.videos_watched}    label="Vidéos visionnées" />
        <StatCard icon="bi-book-open"      color="#0d6efd" value={a.lecons_viewed}     label="Leçons consultées" />
      </div>

      <div className="d-flex gap-3 mt-4">
        <button className="btn btn-primary" onClick={() => onPage("users")}>
          <i className="bi bi-people me-2"></i>Gérer les utilisateurs
        </button>
        <button className="btn btn-outline-secondary" onClick={() => onPage("activite")}>
          <i className="bi bi-graph-up me-2"></i>Voir l'activité
        </button>
      </div>
    </div>
  );
}

// ── User Detail Panel ──────────────────────────────
function UserDetailPanel({ userId, onClose, onRefresh, isSuperAdmin }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSetPwd, setShowSetPwd] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [setPwdLoading, setSetPwdLoading] = useState(false);
  const [setPwdError, setSetPwdError] = useState("");
  const [setPwdOk, setSetPwdOk] = useState(false);
  // Matières
  const [allMatieres,   setAllMatieres]   = useState([]);
  const [selectedMats,  setSelectedMats]  = useState([]);   // ids sélectionnés
  const [matSaving,     setMatSaving]     = useState(false);
  const [matMsg,        setMatMsg]        = useState("");

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    api.get(`admin/users/${userId}/`).then(r => {
      setDetail(r.data);
      setSelectedMats((r.data.matieres || []).map(m => m.id));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  // Charger toutes les matières quand on affiche un enseignant
  useEffect(() => {
    if (detail && (detail.is_staff || detail.is_superuser) && allMatieres.length === 0) {
      api.get("matieres/").then(r => setAllMatieres(r.data.results || r.data)).catch(() => {});
    }
  }, [detail, allMatieres.length]);

  const toggleMat = (id) => setSelectedMats(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const doSetMatieres = async () => {
    setMatSaving(true); setMatMsg("");
    try {
      const r = await api.patch(`admin/users/${userId}/`, { action: "set_matieres", matiere_ids: selectedMats });
      setDetail(prev => ({ ...prev, matieres: r.data.matieres }));
      setMatMsg("Matières enregistrées.");
      setTimeout(() => setMatMsg(""), 2500);
      onRefresh();
    } catch { setMatMsg("Erreur lors de la sauvegarde."); }
    setMatSaving(false);
  };

  const doAction = (action, label) => {
    qlConfirm(`Confirmer : ${label} ?`, async () => {
      setActionLoading(true);
      try {
        await api.patch(`admin/users/${userId}/`, { action });
        if (action === "delete") { onClose(); onRefresh(); return; }
        const r = await api.get(`admin/users/${userId}/`);
        setDetail(r.data);
        onRefresh();
      } catch {}
      setActionLoading(false);
    }, { danger: action === "delete", okLabel: "Confirmer" });
  };

  const doSetPassword = async () => {
    if (!newPwd || newPwd.length < 6) {
      setSetPwdError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    setSetPwdLoading(true);
    setSetPwdError("");
    setSetPwdOk(false);
    try {
      const r = await api.patch(`admin/users/${userId}/`, { action: "set_password", new_password: newPwd });
      setDetail(prev => ({ ...prev, mot_de_passe_clair: r.data.mot_de_passe_clair }));
      setSetPwdOk(true);
      setNewPwd("");
      setTimeout(() => { setShowSetPwd(false); setSetPwdOk(false); }, 1500);
    } catch (e) {
      setSetPwdError(e?.response?.data?.detail || "Erreur lors de la mise à jour.");
    }
    setSetPwdLoading(false);
  };

  if (!userId) return null;
  if (loading) return (
    <aside className="ap-detail-panel">
      <div className="ap-loading"><div className="spinner-border text-primary spinner-border-sm"></div></div>
    </aside>
  );
  if (!detail) return null;

  return (
    <aside className="ap-detail-panel">
      <div className="ap-detail-header">
        <span>Détails de l'utilisateur</span>
        <button className="ap-detail-close" onClick={onClose}><i className="bi bi-x-lg"></i></button>
      </div>

      {/* Avatar + infos */}
      <div className="ap-detail-user">
        {detail.photo_url
          ? <img src={detail.photo_url} alt="" className="ap-detail-avatar-img" />
          : <div className="ap-detail-avatar">{avatarLetters(detail.nom_complet)}</div>
        }
        <div className="ap-detail-user-info">
          <div className="ap-detail-uname">{detail.nom_complet}</div>
          <div className="ap-detail-email">{detail.email}</div>
          <div className="d-flex gap-2 mt-1">
            {roleBadge(detail.role, detail.role_label)}
            {statusBadge(detail.is_active)}
          </div>
        </div>
      </div>

      {/* Infos clés */}
      <div className="ap-detail-rows">
        {[
          { icon: "bi-envelope", label: "Email", val: detail.email },
          { icon: "bi-mortarboard", label: "Classe", val: detail.classe || "–" },
          { icon: "bi-building", label: "École", val: detail.ecole || "–" },
          { icon: "bi-telephone", label: "Téléphone", val: detail.telephone || "–" },
          { icon: "bi-calendar3", label: "Date de naissance", val: detail.date_naissance ? formatDate(detail.date_naissance) : "–" },
          { icon: "bi-calendar-plus", label: "Inscription", val: formatDate(detail.date_joined) },
          { icon: "bi-clock-history", label: "Dernière déconnexion", val: detail.derniere_deconnexion ? formatDate(detail.derniere_deconnexion) : "Jamais" },
          { icon: "bi-hourglass-split", label: "Temps apprentissage", val: `${detail.stats?.learning_minutes ?? 0} min` },
          { icon: "bi-patch-check", label: "Score moyen QCM", val: `${detail.stats?.avg_score ?? 0}%` },
          { icon: "bi-toggle-on", label: "Statut", val: detail.is_active ? "Actif" : "Suspendu" },
        ].map((row, i) => (
          <div key={i} className="ap-detail-row">
            <div className="ap-detail-row-label"><i className={`bi ${row.icon} me-1`}></i>{row.label}</div>
            <div className="ap-detail-row-val">{row.val}</div>
          </div>
        ))}
        <div className="ap-detail-row">
          <div className="ap-detail-row-label"><i className="bi bi-key me-1"></i>Mot de passe</div>
          <div className="ap-detail-row-val">
            {detail.mot_de_passe_clair
              ? <PasswordReveal value={detail.mot_de_passe_clair} />
              : <span className="text-muted" style={{ fontSize: "0.85rem" }}>Non enregistré</span>
            }
          </div>
        </div>
        {detail.code_enseignant && (
          <div className="ap-detail-row">
            <div className="ap-detail-row-label"><i className="bi bi-mortarboard-fill me-1" style={{ color: "#0891b2" }}></i>Code enseignant</div>
            <div className="ap-detail-row-val">
              <code style={{ background: "#e0f2fe", color: "#0c4a6e", padding: "2px 8px", borderRadius: 6, fontSize: "0.9rem", letterSpacing: "0.05em" }}>
                {detail.code_enseignant}
              </code>
            </div>
          </div>
        )}
        {isSuperAdmin && (
          <div className="ap-detail-row flex-column align-items-start gap-1">
            <button
              className="btn btn-outline-secondary btn-sm"
              style={{ fontSize: "0.8rem" }}
              onClick={() => { setShowSetPwd(v => !v); setSetPwdError(""); setSetPwdOk(false); setNewPwd(""); }}
            >
              <i className="bi bi-pencil-square me-1"></i>
              {detail.mot_de_passe_clair ? "Modifier le mot de passe" : "Définir un mot de passe"}
            </button>
            {showSetPwd && (
              <div className="d-flex flex-column gap-1 w-100 mt-1">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Nouveau mot de passe (min. 6 caractères)"
                  value={newPwd}
                  onChange={e => { setNewPwd(e.target.value); setSetPwdError(""); }}
                />
                {setPwdError && <div className="text-danger" style={{ fontSize: "0.8rem" }}>{setPwdError}</div>}
                {setPwdOk && <div className="text-success" style={{ fontSize: "0.8rem" }}>Mot de passe mis à jour.</div>}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={doSetPassword}
                  disabled={setPwdLoading}
                >
                  {setPwdLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  Enregistrer
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Matières enseignées (enseignants uniquement) */}
      {detail.is_staff && !detail.is_superuser && (
        <div className="ap-detail-matieres-section">
          <div className="ap-detail-section-title">
            <i className="bi bi-journal-bookmark me-1"></i>Matières enseignées
          </div>
          {allMatieres.length === 0 ? (
            <div className="text-muted" style={{ fontSize: "0.82rem" }}>Chargement…</div>
          ) : (
            <div className="ap-detail-mat-grid">
              {allMatieres.map(m => (
                <label key={m.id} className={`ap-detail-mat-chip${selectedMats.includes(m.id) ? " selected" : ""}${!isSuperAdmin ? " readonly" : ""}`}>
                  <input
                    type="checkbox"
                    checked={selectedMats.includes(m.id)}
                    onChange={() => isSuperAdmin && toggleMat(m.id)}
                    style={{ display: "none" }}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          )}
          {isSuperAdmin && allMatieres.length > 0 && (
            <div className="d-flex align-items-center gap-2 mt-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={doSetMatieres}
                disabled={matSaving}
              >
                {matSaving ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-check2 me-1"></i>}
                Enregistrer
              </button>
              {matMsg && (
                <span style={{ fontSize: "0.8rem" }} className={matMsg.includes("Erreur") ? "text-danger" : "text-success"}>
                  {matMsg}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Stats activité */}
      <div className="ap-detail-stats-grid">
        {[
          { icon: "bi-book-open", color: "#0d6efd", val: detail.stats?.lecons_read,     label: "Leçons lues" },
          { icon: "bi-play-btn",  color: "#198754", val: detail.stats?.videos_watched,  label: "Vidéos vues" },
          { icon: "bi-pencil",    color: "#6f42c1", val: detail.stats?.exercices_done,  label: "Exercices" },
          { icon: "bi-grid",      color: "#f5820d", val: detail.stats?.qcm_done,        label: "QCM" },
        ].map((s, i) => (
          <div key={i} className="ap-detail-stat">
            <i className={`bi ${s.icon}`} style={{ color: s.color }}></i>
            <div className="ap-detail-stat-val">{s.val ?? 0}</div>
            <div className="ap-detail-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Actions — superadmin uniquement */}
      {isSuperAdmin && (
        <div className="ap-detail-actions">
          {detail.is_active
            ? <button className="btn btn-warning btn-sm w-100" disabled={actionLoading} onClick={() => doAction("suspend", "Suspendre cet utilisateur")}>
                <i className="bi bi-pause-circle me-1"></i>Suspendre
              </button>
            : <button className="btn btn-success btn-sm w-100" disabled={actionLoading} onClick={() => doAction("activate", "Activer cet utilisateur")}>
                <i className="bi bi-check-circle me-1"></i>Activer
              </button>
          }
          {/* Conversion Générateur */}
          {!detail.is_staff && !detail.is_superuser && !detail.is_generateur && (
            <button className="btn btn-sm w-100" style={{ background: "#0891b2", color: "#fff" }}
              disabled={actionLoading}
              onClick={() => qlConfirm(
                "Convertir cet utilisateur en Générateur d'épreuves ? Un code enseignant lui sera automatiquement attribué s'il n'en a pas.",
                async () => {
                  setActionLoading(true);
                  try {
                    const r = await api.patch(`admin/users/${userId}/`, { action: "set_generateur" });
                    const updated = await api.get(`admin/users/${userId}/`);
                    setDetail(updated.data);
                    onRefresh();
                    if (r.data.code_enseignant) qlAlert(`Statut GÉNÉRATEUR attribué.\nCode enseignant : ${r.data.code_enseignant}\nCommuniquez ce code à l'utilisateur.`, "success");
                  } catch {}
                  setActionLoading(false);
                }
              )}>
              <i className="bi bi-person-plus-fill me-1"></i>Convertir en Générateur
            </button>
          )}
          {detail.is_generateur && (
            <button className="btn btn-outline-secondary btn-sm w-100" disabled={actionLoading}
              onClick={() => doAction("remove_generateur", "Retirer le statut Générateur de cet utilisateur")}>
              <i className="bi bi-person-dash me-1"></i>Retirer statut Générateur
            </button>
          )}
          <button className="btn btn-danger btn-sm w-100" disabled={actionLoading} onClick={() => doAction("delete", "Supprimer définitivement cet utilisateur")}>
            <i className="bi bi-trash me-1"></i>Supprimer
          </button>
        </div>
      )}

      {/* Activités récentes */}
      {detail.activities && detail.activities.length > 0 && (
        <div className="ap-detail-activities">
          <div className="ap-detail-section-title">Activités récentes</div>
          {detail.activities.slice(0, 8).map((a, i) => {
            const icons = { qcm: "bi-grid", exercice: "bi-pencil", video: "bi-play-circle", lecon: "bi-file-text" };
            const colors = { qcm: "#6f42c1", exercice: "#f5820d", video: "#198754", lecon: "#0d6efd" };
            return (
              <div key={i} className="ap-detail-act-row">
                <i className={`bi ${icons[a.type] || "bi-circle"}`} style={{ color: colors[a.type] }}></i>
                <div className="ap-detail-act-body">
                  <div className="ap-detail-act-name">{a.name}</div>
                  <div className="ap-detail-act-date">{a.date ? formatDateShort(a.date) : ""}{a.score != null ? ` · ${a.score}%` : ""}</div>
                </div>
                {a.passed != null && (
                  <i className={`bi ${a.passed ? "bi-check-circle-fill text-success" : "bi-x-circle-fill text-danger"}`}></i>
                )}
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}

// ── Modal Créer Utilisateur ────────────────────────
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm]     = useState({ username: "", password: "", nom_complet: "", email: "", role: "eleve", ecole: "", classe_id: "" });
  const [classes, setClasses] = useState([]);
  const [saving, setSaving]  = useState(false);
  const [err, setErr]        = useState("");

  useEffect(() => {
    api.get("classes/").then(r => setClasses(r.data?.results || r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setSaving(true);
    try {
      await api.post("admin/users/", {
        username:    form.username,
        password:    form.password,
        nom_complet: form.nom_complet,
        email:       form.email,
        role:        form.role,
        ecole:       form.ecole,
        classe_id:   form.classe_id || null,
      });
      onCreated();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ap-modal-overlay" onClick={onClose}>
      <div className="ap-modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="ap-modal-header">
          <h2 className="ap-modal-title"><i className="bi bi-person-plus me-2"></i>Créer un utilisateur</h2>
          <button className="ap-modal-close" onClick={onClose}><i className="bi bi-x-lg"></i></button>
        </div>
        <form onSubmit={handleSubmit} className="ap-modal-body">
          {err && <div className="alert alert-danger py-2 mb-3">{err}</div>}
          <div className="mb-3">
            <label className="form-label fw-semibold">Nom complet *</label>
            <input className="form-control" value={form.nom_complet} onChange={e => set("nom_complet", e.target.value)} required />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Nom d'utilisateur *</label>
            <input className="form-control" value={form.username} onChange={e => set("username", e.target.value)} required />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Mot de passe *</label>
            <input className="form-control" type="password" value={form.password} onChange={e => set("password", e.target.value)} required />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Email</label>
            <input className="form-control" type="email" value={form.email} onChange={e => set("email", e.target.value)} />
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">Rôle *</label>
            <select className="form-select" value={form.role} onChange={e => set("role", e.target.value)}>
              <option value="eleve">Élève</option>
              <option value="enseignant">Enseignant</option>
              <option value="admin">Administrateur</option>
            </select>
          </div>
          {form.role === "eleve" && (
            <>
              <div className="mb-3">
                <label className="form-label fw-semibold">École</label>
                <input className="form-control" value={form.ecole} onChange={e => set("ecole", e.target.value)} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Classe</label>
                <select className="form-select" value={form.classe_id} onChange={e => set("classe_id", e.target.value)}>
                  <option value="">— Non renseignée —</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </>
          )}
          <div className="d-flex gap-2 justify-content-end mt-4">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <><span className="spinner-border spinner-border-sm me-2"></span>Création…</> : <><i className="bi bi-person-plus me-1"></i>Créer</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Users Page ─────────────────────────────────────
function UsersPage({ globalSearch, roleFilter: initialRole = "", isSuperAdmin, onAuditUser }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState(globalSearch || "");
  const [roleFilter, setRole]     = useState(initialRole);
  const [statutFilter, setStatut] = useState("");
  const [page, setPage]           = useState(1);
  const [selectedId, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, per_page: 10 });
    if (search)       params.set("search", search);
    if (roleFilter)   params.set("role", roleFilter);
    if (statutFilter) params.set("statut", statutFilter);
    api.get(`admin/users/?${params}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, search, roleFilter, statutFilter]);

  useEffect(() => { setPage(1); }, [search, roleFilter, statutFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (globalSearch !== undefined) setSearch(globalSearch); }, [globalSearch]);

  const gc = data?.global_counts || {};

  return (
    <div className={`ap-users-layout${selectedId ? " with-detail" : ""}`}>
      <div className="ap-page">
        {/* Header */}
        <div className="ap-page-header">
          <div>
            <div className="ap-breadcrumb"><span>Accueil</span><i className="bi bi-chevron-right"></i><span>Utilisateurs</span></div>
            <h1 className="ap-page-title">Utilisateurs</h1>
            <p className="ap-page-desc">Gérez les utilisateurs de la plateforme : consultez, modifiez et suivez leur activité.</p>
          </div>
          {isSuperAdmin && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
              <i className="bi bi-person-plus me-1"></i>Nouvel utilisateur
            </button>
          )}
        </div>

        {/* Stat cards */}
        <div className="ap-users-stats">
          <StatCard icon="bi-people-fill"  color="#0d6efd" value={gc.total}    label="Total utilisateurs" sub={`${gc.active ?? 0} actifs`} />
          <StatCard icon="bi-person-badge" color="#198754" value={gc.teachers} label="Enseignants" sub={gc.teachers ? `${Math.round((gc.teachers/gc.total)*100)}%` : ""} />
          <StatCard icon="bi-mortarboard"  color="#6f42c1" value={gc.eleves}   label="Élèves" sub={gc.eleves ? `${Math.round((gc.eleves/gc.total)*100)}%` : ""} />
          <StatCard icon="bi-diagram-3"    color="#f5820d" value={gc.groupes}  label="Groupes" />
          <StatCard icon="bi-person-x"     color="#dc3545" value={gc.inactive} label="Comptes inactifs" />
        </div>

        {/* Filters */}
        <div className="ap-filter-bar">
          <div className="ap-filter-search">
            <i className="bi bi-search"></i>
            <input
              className="ap-filter-input"
              placeholder="Rechercher un utilisateur…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="ap-filter-select" value={roleFilter} onChange={e => setRole(e.target.value)}>
            <option value="">Tous les rôles</option>
            <option value="eleve">Élèves</option>
            <option value="enseignant">Enseignants</option>
            {/* Un enseignant ne voit que les élèves et les enseignants (filtré aussi côté serveur). */}
            {isSuperAdmin && <>
              <option value="generateur">Générateurs</option>
              <option value="admin">Administrateurs</option>
              <option value="comptable">Comptables</option>
              <option value="parent">Parents</option>
            </>}
          </select>
          <select className="ap-filter-select" value={statutFilter} onChange={e => setStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            <option value="actif">Actif</option>
            <option value="inactif">Suspendu</option>
          </select>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSearch(""); setRole(""); setStatut(""); }}>
            <i className="bi bi-x-circle me-1"></i>Réinitialiser
          </button>
        </div>

        {/* Table */}
        <div className="ap-table-wrap">
          {loading ? (
            <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
          ) : (
            <table className="ap-table">
              <thead>
                <tr>
                  <th>UTILISATEUR</th>
                  <th>RÔLE</th>
                  <th>CLASSE</th>
                  <th>STATUT</th>
                  <th>DERNIÈRE DÉCONNEXION</th>
                  <th>DATE D'INSCRIPTION</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {(data?.users || []).map(u => (
                  <tr
                    key={u.id}
                    className={selectedId === u.id ? "selected" : ""}
                    onClick={() => isSuperAdmin && setSelected(u.id === selectedId ? null : u.id)}
                    style={isSuperAdmin ? { cursor: "pointer" } : {}}
                  >
                    <td>
                      <div className="ap-table-user">
                        {u.photo_url
                          ? <img src={u.photo_url} alt="" className="ap-table-avatar-img" />
                          : <div className="ap-table-avatar">{avatarLetters(u.nom_complet)}</div>
                        }
                        <div>
                          <div className="ap-table-uname">{u.nom_complet}</div>
                          <div className="ap-table-email">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{roleBadge(u.role, u.role_label)}</td>
                    <td><span className="ap-table-classe">{u.classe || "–"}</span></td>
                    <td>{statusBadge(u.is_active)}</td>
                    <td className="ap-table-date">{u.derniere_deconnexion ? formatDate(u.derniere_deconnexion) : "Jamais"}</td>
                    <td className="ap-table-date">{formatDate(u.date_joined)}</td>
                    <td>
                      {isSuperAdmin && (
                        <div className="ap-table-actions">
                          <button className="ap-action-btn" title="Voir" onClick={e => { e.stopPropagation(); setSelected(u.id === selectedId ? null : u.id); }}>
                            <i className="bi bi-eye"></i>
                          </button>
                          {onAuditUser && (u.role === "eleve" || u.role === "comptable") && (
                            <button className="ap-action-btn" title="Journal d'audit" style={{ color: "#7c3aed" }}
                              onClick={e => { e.stopPropagation(); onAuditUser(u.username); }}>
                              <i className="bi bi-shield-lock"></i>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {data && data.users.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-4 text-muted">Aucun utilisateur trouvé</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {data && data.pages > 1 && (
          <div className="ap-pagination">
            <span className="ap-pagination-info">
              Affichage {((page - 1) * 10) + 1} à {Math.min(page * 10, data.total)} sur {data.total} utilisateurs
            </span>
            <div className="ap-pagination-btns">
              <button className="ap-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <i className="bi bi-chevron-left"></i>
              </button>
              {Array.from({ length: Math.min(5, data.pages) }, (_, i) => {
                const p = Math.max(1, Math.min(data.pages - 4, page - 2)) + i;
                return (
                  <button key={p} className={`ap-page-btn${page === p ? " active" : ""}`} onClick={() => setPage(p)}>{p}</button>
                );
              })}
              {data.pages > 5 && page < data.pages - 2 && <span className="ap-page-dots">…</span>}
              {data.pages > 5 && (
                <button className={`ap-page-btn${page === data.pages ? " active" : ""}`} onClick={() => setPage(data.pages)}>{data.pages}</button>
              )}
              <button className="ap-page-btn" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right detail panel — superadmin uniquement */}
      {selectedId && isSuperAdmin && (
        <UserDetailPanel
          userId={selectedId}
          onClose={() => setSelected(null)}
          onRefresh={load}
          isSuperAdmin={isSuperAdmin}
        />
      )}

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { load(); }}
        />
      )}
    </div>
  );
}

// ── Activité Page ──────────────────────────────────
const ACT_ICONS  = { qcm: "bi-grid-3x3", exercice: "bi-pencil", video: "bi-play-circle", lecon: "bi-file-text", devoir: "bi-pencil-square", evaluation: "bi-clipboard-check-fill" };
const ACT_COLORS = { qcm: "#6f42c1",     exercice: "#f5820d",   video: "#198754",        lecon: "#0d6efd",    devoir: "#0d6efd",          evaluation: "#dc2626" };
const ACT_LABELS = { qcm: "QCM",         exercice: "Exercice",  video: "Vidéo",          lecon: "Leçon",      devoir: "Devoir",            evaluation: "Évaluation" };

function ActivitePage({ isSuperAdmin }) {
  const [activities, setActivities]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [search, setSearch]           = useState("");

  useEffect(() => {
    api.get("admin/activites/")
      .then(r => setActivities(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Groupe les activités par utilisateur
  const userMap = {};
  activities.forEach(a => {
    if (!userMap[a.user_id]) {
      userMap[a.user_id] = {
        user_id: a.user_id, user_name: a.user_name, user_classe: a.user_classe,
        counts: { qcm: 0, exercice: 0, video: 0, lecon: 0, devoir: 0, evaluation: 0 }, total: 0,
      };
    }
    userMap[a.user_id].counts[a.type] = (userMap[a.user_id].counts[a.type] || 0) + 1;
    userMap[a.user_id].total += 1;
  });
  const users = Object.values(userMap).sort((a, b) => b.total - a.total);
  const filteredUsers = users.filter(u =>
    (u.user_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (u.user_classe || "").toLowerCase().includes(search.toLowerCase())
  );

  const fetchPDFBlob = (userId) =>
    api.get(`admin/activites/${userId}/pdf/`, { responseType: "blob" })
       .then(r => r.data);

  const downloadPDF = (userId) => {
    setPdfLoading(userId);
    fetchPDFBlob(userId)
      .then(blob => pdfOpen(blob, `rapport_${selectedUser?.user_name?.replace(/\s+/g, "_") || userId}.pdf`))
      .catch(() => {})
      .finally(() => setPdfLoading(null));
  };

  const printPDF = (userId) => {
    setPdfLoading(`print_${userId}`);
    fetchPDFBlob(userId)
      .then(blob => pdfPrint(blob))
      .catch(() => {})
      .finally(() => setPdfLoading(null));
  };

  const userActivities = selectedUser
    ? activities.filter(a => a.user_id === selectedUser.user_id)
    : [];

  // ── Niveau 2 : détail utilisateur ──
  if (selectedUser) return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb">
            <span className="ap-breadcrumb-link" onClick={() => setSelectedUser(null)}>Activité globale</span>
            <i className="bi bi-chevron-right"></i>
            <span>{selectedUser.user_name}</span>
          </div>
          <h1 className="ap-page-title">{selectedUser.user_name}</h1>
          {selectedUser.user_classe && (
            <p className="ap-page-desc"><i className="bi bi-mortarboard me-1"></i>{selectedUser.user_classe}</p>
          )}
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setSelectedUser(null)}>
            <i className="bi bi-arrow-left me-1"></i>Retour
          </button>
          {isSuperAdmin && (
            <>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => downloadPDF(selectedUser.user_id)}
                disabled={pdfLoading === selectedUser.user_id}
              >
                {pdfLoading === selectedUser.user_id
                  ? <span className="spinner-border spinner-border-sm me-1"></span>
                  : <i className="bi bi-download me-1"></i>}
                Télécharger PDF
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() => printPDF(selectedUser.user_id)}
                disabled={pdfLoading === `print_${selectedUser.user_id}`}
              >
                {pdfLoading === `print_${selectedUser.user_id}`
                  ? <span className="spinner-border spinner-border-sm me-1"></span>
                  : <i className="bi bi-printer me-1"></i>}
                Imprimer
              </button>
            </>
          )}
        </div>
      </div>

      {/* Résumé compteurs */}
      <div className="ap-act-summary">
        {Object.entries(selectedUser.counts).map(([type, count]) => (
          <div key={type} className="ap-act-summary-card">
            <i className={`bi ${ACT_ICONS[type]}`} style={{ color: ACT_COLORS[type] }}></i>
            <span className="ap-act-summary-count">{count}</span>
            <span className="ap-act-summary-label">{ACT_LABELS[type]}</span>
          </div>
        ))}
      </div>

      {/* Tableau des activités */}
      <div className="ap-table-wrap">
        <table className="ap-table">
          <thead>
            <tr><th>TYPE</th><th>CONTENU</th><th>SCORE</th><th>DATE</th></tr>
          </thead>
          <tbody>
            {userActivities.map((a, i) => (
              <tr key={i}>
                <td>
                  <span className="ap-act-type" style={{ color: ACT_COLORS[a.type] }}>
                    <i className={`bi ${ACT_ICONS[a.type] || "bi-circle"} me-1`}></i>
                    {ACT_LABELS[a.type] || a.type}
                  </span>
                </td>
                <td>{a.name}</td>
                <td>
                  {a.score != null
                    ? <span style={{ color: a.score >= 50 ? "#198754" : "#dc3545", fontWeight: 600 }}>{a.score}%</span>
                    : a.grade
                      ? <span style={{ color: gradeColor(a.grade), fontWeight: 600 }}>{a.grade}</span>
                      : <span className="text-muted">–</span>}
                </td>
                <td className="ap-table-date">{a.date ? formatDate(a.date) : "–"}</td>
              </tr>
            ))}
            {userActivities.length === 0 && (
              <tr><td colSpan={4} className="text-center py-4 text-muted">Aucune activité</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ── Niveau 1 : liste des utilisateurs ──
  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <h1 className="ap-page-title">Activité globale</h1>
          <p className="ap-page-desc">{users.length} utilisateur(s) actif(s) sur la plateforme</p>
        </div>
      </div>
      <div className="ap-toolbar mb-3">
        <input
          type="text" className="ap-search"
          placeholder="Rechercher un utilisateur ou une classe…"
          value={search} onChange={e => setSearch(e.target.value)}
        />
      </div>
      {loading
        ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        : (
          <div className="ap-act-user-list">
            {filteredUsers.map(u => (
              <div key={u.user_id} className="ap-act-user-row" onClick={() => setSelectedUser(u)}>
                <div className="ap-act-user-avatar">{avatarLetters(u.user_name)}</div>
                <div className="ap-act-user-info">
                  <div className="ap-act-user-name">{u.user_name}</div>
                  {u.user_classe && <div className="ap-act-user-classe"><i className="bi bi-mortarboard me-1"></i>{u.user_classe}</div>}
                </div>
                <div className="ap-act-user-counts">
                  {Object.entries(u.counts).filter(([, v]) => v > 0).map(([type, count]) => (
                    <span key={type} className="ap-act-count-badge" style={{ color: ACT_COLORS[type] }}>
                      <i className={`bi ${ACT_ICONS[type]} me-1`}></i>{count}
                    </span>
                  ))}
                  <span className="badge bg-secondary ms-1">{u.total} total</span>
                </div>
                <i className="bi bi-chevron-right text-muted ms-2"></i>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-muted py-4 text-center">Aucune activité enregistrée.</p>
            )}
          </div>
        )
      }
    </div>
  );
}

// ── Manuels Page ──────────────────────────────────
function ManuelsPage() {
  const [manuels, setManuels]           = useState([]);
  const [modules, setModules]           = useState([]);
  const [lecons, setLecons]             = useState([]);
  const [videos, setVideos]             = useState([]);
  const [qcms, setQcms]                 = useState([]);
  const [exercices, setExercices]       = useState([]);
  const [selectedManuel, setSelectedManuel] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [activeTab, setActiveTab]       = useState("lecons");
  const [playingVideo, setPlayingVideo] = useState(null);
  const [loading, setLoading]           = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("manuels/").then(r => setManuels(r.data?.results || r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedManuel) return;
    setLoading(true);
    api.get(`modules/?manuel=${selectedManuel.id}`).then(r => setModules(r.data?.results || r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [selectedManuel]);

  useEffect(() => {
    if (!selectedModule) return;
    setLoading(true);
    Promise.all([
      api.get(`lecons/?module=${selectedModule.id}`),
      api.get(`videos/?module=${selectedModule.id}`),
      api.get(`qcms/?module=${selectedModule.id}`),
      api.get(`exercices/?module=${selectedModule.id}`),
    ]).then(([lr, vr, qr, er]) => {
      setLecons(lr.data?.results    || lr.data);
      setVideos(vr.data?.results    || vr.data);
      setQcms(qr.data?.results      || qr.data);
      setExercices(er.data?.results || er.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedModule]);

  const selectManuel = (m) => { setSelectedManuel(m); setModules([]); setSelectedModule(null); };
  const selectModule = (m) => { setSelectedModule(m); setActiveTab("lecons"); setPlayingVideo(null); };
  const backToManuels = () => { setSelectedManuel(null); setSelectedModule(null); setModules([]); setLecons([]); setVideos([]); setQcms([]); setExercices([]); setPlayingVideo(null); };
  const backToModules = () => { setSelectedModule(null); setLecons([]); setVideos([]); setQcms([]); setExercices([]); setPlayingVideo(null); };

  /* Niveau 1 — liste des manuels */
  if (!selectedManuel) return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <h1 className="ap-page-title">Manuels</h1>
          <p className="ap-page-desc">Sélectionnez un manuel pour consulter son contenu pédagogique.</p>
        </div>
      </div>
      {loading
        ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        : (
          <div className="ap-manuels-grid">
            {manuels.filter(m => m.published).map(m => (
              <div key={m.id} className="ap-manuel-card" onClick={() => selectManuel(m)}>
                {m.image_url
                  ? <img src={m.image_url} alt={m.title} className="ap-manuel-cover" />
                  : <div className="ap-manuel-cover ap-manuel-cover-placeholder"><i className="bi bi-book"></i></div>
                }
                <div className="ap-manuel-body">
                  <div className="ap-manuel-title">{m.title}</div>
                  <div className="ap-manuel-level">{m.level}</div>
                  {m.classe_nom && (
                    <div style={{ marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4, background: "#eff6ff", color: "#1d4ed8", borderRadius: 6, padding: "2px 8px", fontSize: "0.72rem", fontWeight: 600 }}>
                      <i className="bi bi-mortarboard" style={{ fontSize: "0.68rem" }}></i>
                      {m.classe_nom}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {manuels.filter(m => m.published).length === 0 && <p className="text-muted">Aucun manuel disponible.</p>}
          </div>
        )
      }
    </div>
  );

  /* Niveau 2 — modules du manuel */
  if (!selectedModule) return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb">
            <span className="ap-breadcrumb-link" onClick={backToManuels}>Manuels</span>
            <i className="bi bi-chevron-right"></i>
            <span>{selectedManuel.title}</span>
          </div>
          <h1 className="ap-page-title">{selectedManuel.title}</h1>
          <p className="ap-page-desc">
            {selectedManuel.level}
            {selectedManuel.classe_nom && <> · <i className="bi bi-mortarboard me-1"></i>{selectedManuel.classe_nom}</>}
            {" · "}{modules.length} module(s)
          </p>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={backToManuels}>
          <i className="bi bi-arrow-left me-1"></i>Retour
        </button>
      </div>
      {loading
        ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        : (
          <div className="ap-modules-list">
            {modules.map(m => (
              <div key={m.id} className="ap-module-row" onClick={() => selectModule(m)}>
                <div className="ap-module-icon"><i className="bi bi-layers"></i></div>
                <div className="ap-module-info">
                  <div className="ap-module-title">{m.title}</div>
                  {m.description && <div className="ap-module-desc">{m.description}</div>}
                </div>
                <div className="ap-module-counts">
                  <span><i className="bi bi-file-text me-1"></i>{m.lecons?.length ?? 0}</span>
                  <span><i className="bi bi-play-circle me-1"></i>{m.video_count ?? 0}</span>
                  <span><i className="bi bi-pencil me-1"></i>{m.exercice_count ?? 0}</span>
                  <span><i className="bi bi-grid me-1"></i>{m.qcm_count ?? 0}</span>
                </div>
                <i className="bi bi-chevron-right text-muted"></i>
              </div>
            ))}
            {modules.length === 0 && <p className="text-muted">Aucun module pour ce manuel.</p>}
          </div>
        )
      }
    </div>
  );

  /* Niveau 3 — leçons & vidéos du module */
  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb">
            <span className="ap-breadcrumb-link" onClick={backToManuels}>Manuels</span>
            <i className="bi bi-chevron-right"></i>
            <span className="ap-breadcrumb-link" onClick={backToModules}>{selectedManuel.title}</span>
            <i className="bi bi-chevron-right"></i>
            <span>{selectedModule.title}</span>
          </div>
          <h1 className="ap-page-title">{selectedModule.title}</h1>
        </div>
        <button className="btn btn-outline-secondary btn-sm" onClick={backToModules}>
          <i className="bi bi-arrow-left me-1"></i>Retour
        </button>
      </div>
      <div className="ap-tabs">
        <button className={`ap-tab${activeTab === "lecons" ? " active" : ""}`} onClick={() => setActiveTab("lecons")}>
          <i className="bi bi-file-text me-1"></i>Leçons ({lecons.length})
        </button>
        <button className={`ap-tab${activeTab === "videos" ? " active" : ""}`} onClick={() => setActiveTab("videos")}>
          <i className="bi bi-play-circle me-1"></i>Vidéos ({videos.length})
        </button>
        <button className={`ap-tab${activeTab === "qcms" ? " active" : ""}`} onClick={() => setActiveTab("qcms")}>
          <i className="bi bi-grid-3x3 me-1"></i>QCM ({qcms.length})
        </button>
        <button className={`ap-tab${activeTab === "exercices" ? " active" : ""}`} onClick={() => setActiveTab("exercices")}>
          <i className="bi bi-pencil-square me-1"></i>Exercices ({exercices.length})
        </button>
      </div>
      {loading
        ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        : activeTab === "lecons"
          ? (
            <div className="ap-content-list">
              {lecons.sort((a, b) => a.order - b.order).map(l => (
                <div key={l.id} className="ap-content-row">
                  <div className="ap-content-icon" style={{ color: "#0d6efd" }}><i className="bi bi-file-text"></i></div>
                  <div className="ap-content-info">
                    <div className="ap-content-title">{l.title}</div>
                    {l.description && <div className="ap-content-desc">{l.description}</div>}
                  </div>
                  {l.pdf_url && (
                    <DownloadLink url={l.pdf_url} filename={`${l.title || "lecon"}.pdf`} mimeType="application/pdf" downloadOnWeb={false} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                      <i className="bi bi-file-pdf me-1"></i>PDF
                    </DownloadLink>
                  )}
                </div>
              ))}
              {lecons.length === 0 && <p className="text-muted py-3">Aucune leçon disponible.</p>}
            </div>
          ) : activeTab === "videos"
          ? (
            <div className="ap-video-grid">
              {videos.map(v => (
                <APVideoCard
                  key={v.id}
                  video={v}
                  playing={playingVideo === v.id}
                  onPlay={() => setPlayingVideo(v.id)}
                />
              ))}
              {videos.length === 0 && <p className="text-muted py-3">Aucune vidéo disponible.</p>}
            </div>
          ) : activeTab === "qcms"
          ? (
            <div className="ap-content-list">
              {qcms.map(q => (
                <div key={q.id} className="ap-content-row">
                  <div className="ap-content-icon" style={{ color: "#20c997" }}><i className="bi bi-grid-3x3"></i></div>
                  <div className="ap-content-info">
                    <div className="ap-content-title">{q.title}</div>
                    <div className="ap-content-desc">
                      <i className="bi bi-question-circle me-1"></i>
                      {q.question_count ?? q.questions?.length ?? 0} question(s)
                      {q.time_limit > 0 && <span className="ms-3"><i className="bi bi-clock me-1"></i>{q.time_limit} min</span>}
                    </div>
                  </div>
                  <span className={`badge ${q.published ? "bg-success" : "bg-secondary"}`}>
                    {q.published ? "Publié" : "Brouillon"}
                  </span>
                </div>
              ))}
              {qcms.length === 0 && <p className="text-muted py-3">Aucun QCM disponible.</p>}
            </div>
          ) : (
            <div className="ap-content-list">
              {exercices.map(e => (
                <div key={e.id} className="ap-content-row">
                  <div className="ap-content-icon" style={{ color: "#fd7e14" }}><i className="bi bi-pencil-square"></i></div>
                  <div className="ap-content-info">
                    <div className="ap-content-title">{e.title}</div>
                    <div className="ap-content-desc">
                      {e.pedagogical_type && (
                        <span className="badge bg-warning text-dark me-2">{e.pedagogical_type}</span>
                      )}
                      {e.format && (
                        <span className="badge bg-light text-dark border me-2">{e.format}</span>
                      )}
                    </div>
                  </div>
                  <span className={`badge ${e.published ? "bg-success" : "bg-secondary"}`}>
                    {e.published ? "Publié" : "Brouillon"}
                  </span>
                </div>
              ))}
              {exercices.length === 0 && <p className="text-muted py-3">Aucun exercice disponible.</p>}
            </div>
          )
      }
    </div>
  );
}

// ── Map component (Leaflet direct, sans react-leaflet) ──
function MapView({ locations }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const layerRef     = useRef(null);

  // Carte + fond de tuiles créés une seule fois. Recréer toute la carte à
  // chaque rafraîchissement des positions (toutes les 30s) rechargeait
  // inutilement les tuiles et réinitialisait le zoom/panoramique de l'admin
  // — seuls les marqueurs sont désormais mis à jour, dans l'effet suivant.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    // Bornes larges autour du Cameroun : on empêche de dézoomer jusqu'à voir
    // le reste du monde (carte du Cameroun uniquement, comme demandé).
    const cameroonBounds = L.latLngBounds([[1.0, 7.3], [14.0, 17.2]]);
    const map = L.map(containerRef.current, {
      scrollWheelZoom: true,
      minZoom: 6,
      maxBounds: cameroonBounds,
      maxBoundsViscosity: 1.0,
    }).setView([4.05, 12.35], 6);
    L.tileLayer(CAMEROON_MAP_TILE_URL, CAMEROON_MAP_TILE_OPTIONS).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const resize = () => map.invalidateSize(false);
    window.addEventListener("resize", resize);
    const initialResize = window.setTimeout(resize, 0);
    return () => {
      window.clearTimeout(initialResize);
      window.removeEventListener("resize", resize);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    locations.forEach(loc => {
      const baseColor = loc.role === "enseignant" ? "#f5820d" : "#3b82f6";
      const color     = loc.is_online ? baseColor : "#94a3b8";
      const fill      = loc.is_online ? baseColor : "#cbd5e1";

      const marker = L.circleMarker([loc.lat, loc.lng], {
        radius:      loc.is_online ? 10 : 7,
        color,
        fillColor:   fill,
        fillOpacity: loc.is_online ? 0.9 : 0.6,
        weight:      loc.is_online ? 2 : 1,
      }).addTo(layer);

      const onlineBadge = loc.is_online
        ? `<span style="background:#dcfce7;color:#16a34a;padding:1px 7px;border-radius:10px;font-size:0.72rem;font-weight:600">● En ligne</span>`
        : `<span style="background:#f1f5f9;color:#64748b;padding:1px 7px;border-radius:10px;font-size:0.72rem;font-weight:600">○ Hors ligne</span>`;

      const lastConn = loc.last_login
        ? `<div style="font-size:0.75rem;color:#94a3b8;margin-top:4px">Connexion : ${new Date(loc.last_login).toLocaleString("fr-FR")}</div>`
        : "";
      const lastDeconn = !loc.is_online && loc.derniere_deconnexion
        ? `<div style="font-size:0.75rem;color:#94a3b8">Déconnexion : ${new Date(loc.derniere_deconnexion).toLocaleString("fr-FR")}</div>`
        : "";

      marker.bindPopup(`
        <div style="min-width:180px">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">
            <strong>${loc.nom_complet}</strong>${onlineBadge}
          </div>
          ${loc.ecole  ? `<div style="font-size:0.82rem">🏫 ${loc.ecole}</div>` : ""}
          ${loc.ville  ? `<div style="font-size:0.82rem">📍 ${loc.ville}</div>`  : ""}
          ${loc.classe ? `<div style="font-size:0.82rem">📚 ${loc.classe}</div>` : ""}
          <div style="font-size:0.78rem;color:#64748b;margin-top:4px">${loc.role === "enseignant" ? "👨‍🏫 Enseignant" : "👨‍🎓 Élève"}</div>
          ${lastConn}${lastDeconn}
        </div>
      `);
    });
  }, [locations]);

  return <div ref={containerRef} style={{ height: "100%", width: "100%" }} />;
}

// ── Rapports Page ─────────────────────────────────
const PERIODES = [
  { key: "daily",   label: "30 derniers jours" },
  { key: "weekly",  label: "16 dernières semaines" },
  { key: "monthly", label: "12 derniers mois" },
  { key: "annual",  label: "5 dernières années" },
];

const CHART_COLORS = ["#3b82f6","#f5820d","#10b981","#8b5cf6","#ef4444","#06b6d4","#f59e0b"];

function fmt(n) {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + " M";
  if (n >= 1000)    return (n / 1000).toFixed(1) + " K";
  return n.toString();
}
function fmtFull(n) {
  return (n ?? 0).toLocaleString("fr-FR");
}

function EnseignantsPDFButtons() {
  const [loading, setLoading] = useState(null);

  const getPDF = () =>
    api.get("admin/rapports/enseignants/pdf/", { responseType: "blob" }).then(r => r.data);

  const download = () => {
    setLoading("dl");
    getPDF()
      .then(blob => pdfOpen(blob, "rapport_enseignants.pdf"))
      .catch(() => {})
      .finally(() => setLoading(null));
  };

  const print = () => {
    setLoading("pr");
    getPDF()
      .then(blob => pdfPrint(blob))
      .catch(() => {})
      .finally(() => setLoading(null));
  };

  return (
    <div className="d-flex gap-2">
      <button className="btn btn-primary btn-sm" onClick={download} disabled={!!loading}>
        {loading === "dl" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-download me-1"></i>}
        Télécharger PDF
      </button>
      <button className="btn btn-outline-primary btn-sm" onClick={print} disabled={!!loading}>
        {loading === "pr" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-printer me-1"></i>}
        Imprimer
      </button>
    </div>
  );
}

// ── Demandes d'assistance (enseignant) ─────────────
function AssistancesPage({ user }) {
  const [requests,   setRequests]   = useState([]);
  const [tab,        setTab]        = useState("pending");
  const [loading,    setLoading]    = useState(true);
  const [expanded,   setExpanded]   = useState(null);

  const load = (filter) => {
    setLoading(true);
    const resolved = filter === "resolved" ? "true" : "false";
    api.get(`assistances/?resolved=${resolved}`)
      .then(r => { setRequests(r.data); rafraichirCompteurs(); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(tab); }, [tab]);

  const markResolved = async (id, val) => {
    await api.patch(`assistances/${id}/`, { resolved: val });
    load(tab);
  };

  const openConversation = async (studentId) => {
    try {
      await api.post("messages/", { participant_id: studentId });
    } catch {}
    window.dispatchEvent(new CustomEvent("ql-open-page", { detail: "messages" }));
  };

  const fmt = iso => new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="ap-section-wrap">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="fw-bold mb-0" style={{ color: "#0f172a" }}>
          <i className="bi bi-question-circle me-2 text-primary"></i>Demandes d'assistance
        </h4>
      </div>
      <div className="ap-assist-tabs mb-3">
        <button className={`ap-assist-tab${tab === "pending" ? " active" : ""}`} onClick={() => setTab("pending")}>
          <i className="bi bi-hourglass-split me-1"></i>En attente
        </button>
        <button className={`ap-assist-tab${tab === "resolved" ? " active" : ""}`} onClick={() => setTab("resolved")}>
          <i className="bi bi-check2-all me-1"></i>Résolues
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-inbox" style={{ fontSize: "2.5rem", color: "#cbd5e1" }}></i>
          <p className="mt-3">Aucune demande {tab === "pending" ? "en attente" : "résolue"}</p>
        </div>
      ) : (
        <div className="ap-assist-list">
          {requests.map(r => (
            <div key={r.id} className={`ap-assist-card${expanded === r.id ? " expanded" : ""}`}>
              <div className="ap-assist-card-header" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                <div className="ap-assist-card-av">
                  {r.student_photo
                    ? <img src={r.student_photo} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }} />
                    : <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#dbeafe", color: "#1d4ed8", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>
                        {(r.student_name || "?")[0].toUpperCase()}
                      </div>
                  }
                </div>
                <div className="ap-assist-card-info">
                  <div className="ap-assist-card-student">{r.student_name}</div>
                  <div className="ap-assist-card-exercise">
                    <i className="bi bi-pencil-square me-1 text-muted"></i>{r.exercice_title}
                    {r.module_title && <span className="text-muted ms-2" style={{ fontSize: "0.75rem" }}>— {r.module_title}</span>}
                  </div>
                </div>
                <div className="ap-assist-card-meta">
                  <span className="ap-assist-card-date">{fmt(r.created_at)}</span>
                  <i className={`bi ${expanded === r.id ? "bi-chevron-up" : "bi-chevron-down"} ms-2 text-muted`}></i>
                </div>
              </div>

              {expanded === r.id && (
                <div className="ap-assist-card-body">
                  <div className="ap-assist-answer-label">Réponse de l'élève :</div>
                  <div className="ap-assist-answer-text">
                    {r.student_answer || <span className="text-muted fst-italic">Aucune réponse rédigée</span>}
                  </div>
                  {r.resolved && r.resolved_by_id && r.resolved_by_id !== user?.id && (
                    <div className="ap-assist-already-done">
                      <i className="bi bi-check2-circle me-2"></i>
                      Déjà assisté par <strong>Professeur {r.resolved_by_name}</strong>
                    </div>
                  )}
                  <div className="ap-assist-card-actions">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openConversation(r.student_id)}>
                      <i className="bi bi-chat-dots me-1"></i>Contacter l'élève
                    </button>
                    {r.resolved && r.resolved_by_id && r.resolved_by_id !== user?.id ? null : !r.resolved ? (
                      <button className="btn btn-sm btn-success" onClick={() => markResolved(r.id, true)}>
                        <i className="bi bi-check2-circle me-1"></i>Marquer comme résolu
                      </button>
                    ) : (
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => markResolved(r.id, false)}>
                        <i className="bi bi-arrow-counterclockwise me-1"></i>Rouvrir
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Rapport Hebdomadaire Enseignant ────────────────
const TACHES_DEF = [
  { key: "assister_eleves",    label: "Assister des élèves" },
  { key: "cours_en_ligne",     label: "Mettre des cours en ligne",  hasCount: true, countKey: "nb_cours" },
  { key: "videos_en_ligne",    label: "Mettre des vidéos en ligne", hasCount: true, countKey: "nb_videos" },
  { key: "ajouter_exercices",  label: "Ajouter des exercices",      hasCount: true, countKey: "nb_exercices" },
  { key: "corriger_exercices", label: "Corriger des exercices",     hasCount: true, countKey: "nb_corrections" },
  { key: "repondre_questions", label: "Répondre aux questions des élèves" },
  { key: "creer_qcm",         label: "Créer des QCM",              hasCount: true, countKey: "nb_qcm" },
];

function RapportEnseignantPage({ user }) {
  const [taches,      setTaches]      = useState({});
  const [counts,      setCounts]      = useState({ nb_cours: 0, nb_videos: 0, nb_exercices: 0, nb_corrections: 0, nb_qcm: 0 });
  const [difficultes, setDifficultes] = useState("");
  const [suggestions, setSuggestions] = useState("");
  const [sending,     setSending]     = useState(false);
  const [sent,        setSent]        = useState(false);
  const [err,         setErr]         = useState("");

  const now       = new Date();
  const dateStr   = now.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  const heureStr  = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const nomEns    = user?.nom_complet || user?.username || "Enseignant";

  const toggleTache = (key) => setTaches(t => ({ ...t, [key]: !t[key] }));
  const setCount    = (key, v) => setCounts(c => ({ ...c, [key]: Math.max(0, parseInt(v) || 0) }));

  const handleSubmit = async () => {
    setSending(true); setErr("");
    try {
      await api.post("rapports/enseignant/", {
        taches,
        difficultes,
        suggestions,
        nb_cours:       counts.nb_cours,
        nb_videos:      counts.nb_videos,
        nb_exercices:   counts.nb_exercices,
        nb_corrections: counts.nb_corrections,
        nb_qcm:         counts.nb_qcm,
      });
      setSent(true);
    } catch (e) {
      setErr(e.response?.data?.detail || "Erreur lors de l'envoi du rapport.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="ap-page">
        <div className="rap-success">
          <div className="rap-success-icon"><i className="bi bi-check-circle-fill"></i></div>
          <h2 className="rap-success-title">Rapport envoyé avec succès !</h2>
          <p className="rap-success-sub">Votre rapport hebdomadaire a été transmis à la hiérarchie.</p>
          <button className="btn btn-outline-primary mt-3" onClick={() => { setSent(false); setTaches({}); setCounts({ nb_cours: 0, nb_videos: 0, nb_exercices: 0, nb_corrections: 0, nb_qcm: 0 }); setDifficultes(""); setSuggestions(""); }}>
            <i className="bi bi-plus-circle me-1"></i>Nouveau rapport
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Accueil</span><i className="bi bi-chevron-right"></i><span>Rapport hebdomadaire</span></div>
          <h1 className="ap-page-title">Rapport hebdomadaire de l'enseignant {nomEns}</h1>
          <p className="ap-page-desc">
            <i className="bi bi-calendar3 me-1"></i>{dateStr} à {heureStr}
          </p>
        </div>
      </div>

      <div className="rap-form">
        {err && <div className="alert alert-danger mb-4">{err}</div>}

        {/* Partie I */}
        <div className="rap-section">
          <div className="rap-section-num">PARTIE I</div>
          <h3 className="rap-section-title">TÂCHES EFFECTUÉES</h3>
          <p className="rap-section-hint">Cochez les tâches que vous avez effectuées cette semaine.</p>
          <div className="rap-checklist">
            {TACHES_DEF.map(t => (
              <div key={t.key} className={`rap-check-item${taches[t.key] ? " checked" : ""}`}>
                <label className="rap-check-label">
                  <input
                    type="checkbox"
                    className="rap-checkbox"
                    checked={!!taches[t.key]}
                    onChange={() => toggleTache(t.key)}
                  />
                  <span className="rap-check-text">{t.label}</span>
                </label>
                {t.hasCount && (
                  <input
                    type="number"
                    min="0"
                    className="rap-count-input"
                    value={counts[t.countKey]}
                    onChange={e => setCount(t.countKey, e.target.value)}
                    title="Nombre"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Partie II */}
        <div className="rap-section">
          <div className="rap-section-num">PARTIE II</div>
          <h3 className="rap-section-title">DIFFICULTÉS RENCONTRÉES</h3>
          <p className="rap-section-hint">Décrivez les difficultés que vous avez rencontrées cette semaine.</p>
          <textarea
            className="rap-textarea"
            rows={5}
            placeholder="Ex : Manque de ressources pédagogiques, difficultés techniques sur la plateforme…"
            value={difficultes}
            onChange={e => setDifficultes(e.target.value)}
          />
        </div>

        {/* Partie III */}
        <div className="rap-section">
          <div className="rap-section-num">PARTIE III</div>
          <h3 className="rap-section-title">SUGGESTIONS ET SOLLICITATIONS À LA HIÉRARCHIE</h3>
          <p className="rap-section-hint">Partagez vos suggestions ou sollicitations à l'attention de la direction.</p>
          <textarea
            className="rap-textarea"
            rows={5}
            placeholder="Ex : Demande de formation, suggestion d'amélioration de la plateforme…"
            value={suggestions}
            onChange={e => setSuggestions(e.target.value)}
          />
        </div>

        {/* Bouton envoi */}
        <div className="rap-submit-row">
          <p className="rap-submit-note">
            <i className="bi bi-info-circle me-1"></i>
            Ce rapport sera transmis à la hiérarchie sous forme de PDF téléchargeable.
          </p>
          <button className="btn btn-primary btn-lg rap-submit-btn" onClick={handleSubmit} disabled={sending}>
            {sending
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Envoi en cours…</>
              : <><i className="bi bi-send-fill me-2"></i>Envoyer le rapport</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function RapportsPage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);
  const [periode, setPeriode] = useState("monthly");
  const [backfilling, setBackfilling] = useState(false);

  const handleBackfill = () => {
    qlConfirm("Créer les paiements manquants pour tous les codes manuels déjà activés ?", () => {
      setBackfilling(true);
      api.post("admin/backfill-manuels/")
        .then(r => {
          qlAlert(r.data.message, "success");
          setLoading(true);
          return api.get(`admin/rapports/?periode=${periode}`);
        })
        .then(r => setData(r.data))
        .catch(() => qlAlert("Erreur lors du remplissage.", "error"))
        .finally(() => { setBackfilling(false); setLoading(false); });
    });
  };

  useEffect(() => {
    const load = (showSpinner) => {
      if (showSpinner) { setLoading(true); setError(false); }
      api.get(`admin/rapports/?periode=${periode}`)
        .then(r => setData(r.data))
        .catch(() => { if (showSpinner) setError(true); })
        .finally(() => { if (showSpinner) setLoading(false); });
    };
    load(true);
    // Rafraîchit automatiquement (ex. abonnement ajouté depuis l'admin Django
    // pendant que cette page reste ouverte) sans attendre un rechargement manuel.
    const id = setInterval(() => load(false), 30000);
    return () => clearInterval(id);
  }, [periode]);

  if (loading) return (
    <div className="ap-page">
      <div className="ap-page-header"><h1 className="ap-page-title">Rapports</h1></div>
      <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
    </div>
  );

  if (error || !data) return (
    <div className="ap-page">
      <div className="ap-page-header"><h1 className="ap-page-title">Rapports</h1></div>
      <div className="ap-loading">
        <p className="text-danger">Impossible de charger les rapports. Vérifiez que le serveur est démarré.</p>
      </div>
    </div>
  );

  const { revenus, totaux, forfaits, methodes, ecoles, enseignants, locations } = data;

  /* ── Graphique revenus ── */
  const hasManuels = (revenus.manuels || []).some(v => v > 0);
  const lineData = {
    labels: revenus.labels,
    datasets: [
      {
        label: "Revenus totaux (FCFA)",
        data: revenus.data,
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59,130,246,0.08)",
        tension: 0.4, fill: true, pointRadius: 4,
        pointBackgroundColor: "#3b82f6",
      },
      ...(hasManuels ? [{
        label: "Vente Manuel (FCFA)",
        data: revenus.manuels,
        borderColor: "#10b981",
        backgroundColor: "rgba(16,185,129,0.10)",
        tension: 0.4, fill: true, pointRadius: 4,
        pointBackgroundColor: "#10b981",
        borderDash: [5, 3],
      }] : []),
    ],
  };
  const lineOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: hasManuels, position: "top", labels: { font: { size: 11 } } } },
    scales: {
      y: { beginAtZero: true, ticks: { callback: v => fmt(v) + " F" } },
      x: { grid: { display: false } },
    },
  };

  /* ── Donut forfaits ── */
  const donutData = {
    labels: forfaits.map(f => f.label),
    datasets: [{ data: forfaits.map(f => f.count), backgroundColor: CHART_COLORS, borderWidth: 2 }],
  };
  const donutOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: "bottom", labels: { font: { size: 11 } } } },
  };

  /* ── Barres méthodes ── */
  const _methodColor = {
    "MTN Mobile Money":    "#3b82f6",
    "Orange Money":        "#f5820d",
    "Admin Django":        "#6366f1",
    "Génération d'épreuves": "#10b981",
    "Vente Manuel":        "#059669",
  };
  const methodsData = {
    labels: methodes.map(m => m.label),
    datasets: [{
      label: "FCFA perçus",
      data: methodes.map(m => m.total),
      backgroundColor: methodes.map(m => _methodColor[m.label] || "#10b981"),
      borderRadius: 6,
    }],
  };
  const methodsOpts = {
    indexAxis: "y",
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { ticks: { callback: v => fmt(v) + " F" } } },
  };

  /* ── Barres écoles ── */
  const ecolesData = {
    labels: ecoles.map(e => e.ecole.length > 25 ? e.ecole.slice(0, 25) + "…" : e.ecole),
    datasets: [{
      label: "Élèves",
      data: ecoles.map(e => e.count),
      backgroundColor: ecoles.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
      borderRadius: 5,
    }],
  };
  const ecolesOpts = {
    indexAxis: "y",
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } },
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Statistiques</span><i className="bi bi-chevron-right"></i><span>Rapports</span></div>
          <h1 className="ap-page-title">Rapports & Statistiques</h1>
          <p className="ap-page-desc">Vue d'ensemble financière, abonnements, répartition géographique et activité enseignants.</p>
        </div>
        <button className="ap-btn ap-btn-secondary" onClick={handleBackfill} disabled={backfilling}
          title="Créer les entrées de revenus manquantes pour les codes manuels déjà activés"
          style={{ fontSize: ".8rem", gap: 6 }}>
          <i className="bi bi-arrow-repeat"></i>
          {backfilling ? "En cours…" : "Sync revenus manuels"}
        </button>
      </div>

      {/* ── Totaux revenus ── */}
      <div className="ap-rapport-totaux">
        {[
          { label: "Aujourd'hui",  val: totaux.jour,    icon: "bi-sun",          color: "#f5820d" },
          { label: "Cette semaine",val: totaux.semaine, icon: "bi-calendar-week", color: "#3b82f6" },
          { label: "Ce mois",      val: totaux.mois,    icon: "bi-calendar-month",color: "#10b981" },
          { label: "Cette année",  val: totaux.annee,   icon: "bi-calendar",      color: "#8b5cf6" },
          { label: "Total",        val: totaux.total,   icon: "bi-cash-stack",    color: "#ef4444" },
        ].map((t, i) => (
          <div key={i} className="ap-rapport-total-card">
            <div className="ap-rapport-total-icon" style={{ background: t.color + "18", color: t.color }}>
              <i className={`bi ${t.icon}`}></i>
            </div>
            <div>
              <div className="ap-rapport-total-val">{fmtFull(t.val)} FCFA</div>
              <div className="ap-rapport-total-label">{t.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Graphique revenus ── */}
      <div className="ap-rapport-section">
        <div className="ap-rapport-section-header">
          <h2 className="ap-rapport-section-title"><i className="bi bi-graph-up me-2"></i>Évolution des revenus</h2>
          <div className="ap-rapport-periode-btns">
            {PERIODES.map(p => (
              <button
                key={p.key}
                className={`ap-periode-btn${periode === p.key ? " active" : ""}`}
                onClick={() => setPeriode(p.key)}
              >{p.label}</button>
            ))}
          </div>
        </div>
        <div className="ap-rapport-chart-wrap" style={{ height: 280 }}>
          {revenus.labels.length > 0
            ? <Line data={lineData} options={lineOpts} />
            : <div className="ap-rapport-empty"><i className="bi bi-bar-chart-line"></i><p>Aucune donnée pour cette période</p></div>
          }
        </div>
      </div>

      {/* ── Forfaits + méthodes ── */}
      <div className="ap-rapport-row2">
        <div className="ap-rapport-section">
          <h2 className="ap-rapport-section-title"><i className="bi bi-pie-chart me-2"></i>Répartition des forfaits</h2>
          <div className="ap-rapport-chart-wrap" style={{ height: 240 }}>
            {forfaits.length > 0
              ? <Doughnut data={donutData} options={donutOpts} />
              : <div className="ap-rapport-empty"><i className="bi bi-pie-chart"></i><p>Aucun abonnement enregistré</p></div>
            }
          </div>
        </div>
        <div className="ap-rapport-section">
          <h2 className="ap-rapport-section-title"><i className="bi bi-phone me-2"></i>Méthodes de paiement</h2>
          <div className="ap-rapport-chart-wrap" style={{ height: Math.max(180, methodes.length * 70) }}>
            {methodes.length > 0
              ? <Bar data={methodsData} options={methodsOpts} />
              : <div className="ap-rapport-empty"><i className="bi bi-phone"></i><p>Aucun paiement enregistré</p></div>
            }
          </div>
          {methodes.length > 0 && (
            <table className="ap-rapport-mini-table mt-3">
              <tbody>
                {methodes.map((m, i) => (
                  <tr key={i}>
                    <td><span className="ap-rapport-dot" style={{ background: _methodColor[m.label] || "#10b981" }}></span>{m.label}</td>
                    <td className="text-end fw-semibold">{m.count} trans.</td>
                    <td className="text-end fw-semibold">{fmtFull(m.total)} FCFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Carte GPS ── */}
      <div className="ap-rapport-section">
        <div className="ap-rapport-section-header">
          <h2 className="ap-rapport-section-title" style={{ margin: 0 }}>
            <i className="bi bi-geo-alt me-2"></i>Localisation des utilisateurs
          </h2>
          <div className="d-flex gap-3 align-items-center" style={{ fontSize: "0.8rem" }}>
            <span className="ap-rapport-note">{locations.length} position(s) enregistrée(s)</span>
            <span className="ap-map-legend-item">
              <span className="ap-map-dot online"></span>En ligne ({locations.filter(l => l.is_online).length})
            </span>
            <span className="ap-map-legend-item">
              <span className="ap-map-dot offline"></span>Hors ligne ({locations.filter(l => !l.is_online).length})
            </span>
          </div>
        </div>
        <div style={{ height: 460, borderRadius: 10, overflow: "hidden", marginTop: 12, border: "1px solid #e2e8f0" }}>
          {locations.length > 0 ? (
            <MapView locations={locations} />
          ) : (
            <div className="ap-rapport-empty" style={{ height: "100%", background: "#f8fafc" }}>
              <i className="bi bi-geo-alt" style={{ fontSize: "2.5rem" }}></i>
              <p>Aucune position GPS enregistrée pour l'instant.</p>
              <p className="text-muted small">Les coordonnées sont capturées automatiquement à la connexion si l'utilisateur autorise la géolocalisation.</p>
            </div>
          )}
        </div>
        <div className="d-flex gap-4 mt-2 flex-wrap" style={{ fontSize: "0.8rem", color: "#475569" }}>
          <span><span className="ap-rapport-dot" style={{ background: "#3b82f6" }}></span>Élèves en ligne</span>
          <span><span className="ap-rapport-dot" style={{ background: "#f5820d" }}></span>Enseignants en ligne</span>
          <span><span className="ap-rapport-dot" style={{ background: "#cbd5e1", border: "1px solid #94a3b8" }}></span>Hors ligne (dernière position connue)</span>
        </div>
      </div>

      {/* ── Répartition par école ── */}
      <div className="ap-rapport-section">
        <h2 className="ap-rapport-section-title">
          <i className="bi bi-building me-2"></i>Top établissements
          <span className="ap-rapport-note">Top 15</span>
        </h2>
        <div className="ap-rapport-chart-wrap" style={{ height: Math.max(200, ecoles.length * 36) }}>
          {ecoles.length > 0
            ? <Bar data={ecolesData} options={ecolesOpts} />
            : <div className="ap-rapport-empty"><i className="bi bi-building"></i><p>Aucune école enregistrée</p></div>
          }
        </div>
      </div>

      {/* ── Tableau enseignants ── */}
      <div className="ap-rapport-section">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <h2 className="ap-rapport-section-title mb-0"><i className="bi bi-person-badge me-2"></i>Activité des enseignants</h2>
          <EnseignantsPDFButtons />
        </div>
        {enseignants.length === 0
          ? <p className="text-muted">Aucun enseignant enregistré.</p>
          : (
            <div className="ap-table-wrap">
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>ENSEIGNANT</th>
                    <th>DERNIÈRE CONNEXION</th>
                    <th>DERNIÈRE DÉCONNEXION</th>
                    <th>TEMPS SUR LA PLATEFORME</th>
                    <th>RAPPORT</th>
                  </tr>
                </thead>
                <tbody>
                  {enseignants.map((e, i) => {
                    const h = Math.floor(e.total_minutes / 60);
                    const m = e.total_minutes % 60;
                    return (
                      <tr key={i}>
                        <td>
                          <div className="ap-table-uname">{e.nom_complet}</div>
                          <div className="ap-table-email">{e.username}</div>
                        </td>
                        <td className="ap-table-date">{e.last_login ? formatDate(e.last_login) : "Jamais"}</td>
                        <td className="ap-table-date">{e.derniere_deconnexion ? formatDate(e.derniere_deconnexion) : "Jamais"}</td>
                        <td>
                          <span className="ap-rapport-heures">
                            {h > 0 ? `${h}h ` : ""}{m}min
                          </span>
                        </td>
                        <td>
                          <DownloadLink
                            url={e.user_id
                              ? `${api.defaults.baseURL}admin/rapports/enseignants/${e.user_id}/pdf/`
                              : process.env.REACT_APP_WEB_BASE_URL || "/"}
                            filename={`rapport_enseignant_${e.user_id}.pdf`}
                            mimeType="application/pdf"
                            authenticated
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm"
                            title="Télécharger le rapport PDF"
                          >
                            <i className="bi bi-download"></i>
                          </DownloadLink>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  );
}

// ── Codes Manuels Page (superadmin only) ──────────
function CodesPage() {
  const [codes, setCodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState("");
  const [pdfLoading, setPdfLoading] = useState(null);
  const [manuels, setManuels] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const [genManuel, setGenManuel] = useState("");
  const [genQty, setGenQty]   = useState(10);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult]   = useState(null);
  const [genError, setGenError]     = useState("");

  const getPDFBlob = () =>
    api.get("admin/codes/pdf/", { responseType: "blob" }).then(r => r.data);

  const downloadPDF = () => {
    setPdfLoading("dl");
    getPDFBlob()
      .then(blob => pdfOpen(blob, "codes_manuels.pdf"))
      .catch(() => {})
      .finally(() => setPdfLoading(null));
  };

  const printPDF = () => {
    setPdfLoading("pr");
    getPDFBlob()
      .then(blob => pdfPrint(blob))
      .catch(() => {})
      .finally(() => setPdfLoading(null));
  };

  const loadCodes = () => {
    setLoading(true);
    api.get("admin/codes/").then(r => setCodes(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCodes();
    api.get("manuels/").then(r => setManuels(r.data?.results || r.data)).catch(() => {});
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenError(""); setGenResult(null); setGenLoading(true);
    try {
      const r = await api.post("admin/codes/", { manuel_id: genManuel, quantite: genQty });
      setGenResult(r.data);
      loadCodes();
    } catch(err) {
      setGenError(err?.response?.data?.detail || "Erreur lors de la génération.");
    } finally { setGenLoading(false); }
  };

  const handleDeleteCode = (id) => {
    qlConfirm("Supprimer ce code non activé ?", async () => {
      try {
        await api.delete("admin/codes/", { data: { code_id: id } });
        loadCodes();
      } catch(err) {
        qlAlert(err?.response?.data?.detail || "Erreur.", "error");
      }
    }, { danger: true, okLabel: "Supprimer" });
  };

  const [showPrix, setShowPrix] = useState(false);
  const [prixMap, setPrixMap]   = useState({});
  const [savingPrix, setSavingPrix] = useState({});

  const handleSavePrix = async (manuelId) => {
    setSavingPrix(p => ({...p, [manuelId]: true}));
    try {
      await api.patch(`manuels/${manuelId}/`, { prix: prixMap[manuelId] || 0 });
      setManuels(prev => prev.map(m => m.id === manuelId ? { ...m, prix: prixMap[manuelId] || 0 } : m));
    } catch {}
    setSavingPrix(p => ({...p, [manuelId]: false}));
  };

  const filtered = codes.filter(c => {
    const q = search.toLowerCase();
    return !q
      || c.code.toLowerCase().includes(q)
      || (c.owner_nom_complet || "").toLowerCase().includes(q)
      || (c.manuel || "").toLowerCase().includes(q)
      || (c.owner_ecole || "").toLowerCase().includes(q)
      || (c.owner_classe || "").toLowerCase().includes(q);
  });

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Administration</span><i className="bi bi-chevron-right"></i><span>Codes Manuels</span></div>
          <h1 className="ap-page-title">Codes Manuels</h1>
          <p className="ap-page-desc">Association de chaque code manuel à son propriétaire. Visible uniquement par le super administrateur.</p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button className="btn btn-success btn-sm" onClick={() => { setShowGen(!showGen); setGenResult(null); setGenError(""); }}>
            <i className="bi bi-plus-circle me-1"></i>Générer des codes
          </button>
          <button className="btn btn-primary btn-sm" onClick={downloadPDF} disabled={!!pdfLoading}>
            {pdfLoading === "dl" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-download me-1"></i>}
            Télécharger PDF
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={printPDF} disabled={!!pdfLoading}>
            {pdfLoading === "pr" ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-printer me-1"></i>}
            Imprimer
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <button className="btn btn-outline-warning btn-sm" onClick={() => {
          setShowPrix(!showPrix);
          if (!showPrix) setPrixMap(Object.fromEntries(manuels.map(m => [m.id, m.prix || 0])));
        }}>
          <i className="bi bi-tag me-1"></i>Prix des manuels
        </button>
      </div>

      {showPrix && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,.08)", marginBottom: 20 }}>
          <h6 className="fw-bold mb-3"><i className="bi bi-tag me-2"></i>Définir le prix de chaque manuel (FCFA)</h6>
          <p style={{ fontSize: ".82rem", color: "#666", marginBottom: 12 }}>Ce prix sera automatiquement enregistré comme revenu quand un étudiant activera un code de ce manuel.</p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {manuels.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <span style={{ minWidth: 220, fontWeight: 600, fontSize: ".9rem" }}>{m.title} <span style={{ color: "#888", fontWeight: 400 }}>— {m.level}</span></span>
                <input
                  type="number"
                  style={{ width: 130 }}
                  className="form-control form-control-sm"
                  value={prixMap[m.id] ?? m.prix ?? 0}
                  onChange={e => setPrixMap(p => ({...p, [m.id]: parseInt(e.target.value) || 0}))}
                  min={0}
                  placeholder="0 FCFA"
                />
                <button className="btn btn-success btn-sm" disabled={savingPrix[m.id]} onClick={() => handleSavePrix(m.id)}>
                  {savingPrix[m.id] ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-check-lg"></i>}
                </button>
                {m.prix > 0 && <span style={{ fontSize: ".8rem", color: "#22c55e" }}>{new Intl.NumberFormat("fr-FR").format(m.prix)} FCFA</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {showGen && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,.1)", marginBottom: 20 }}>
          <h6 className="fw-bold mb-3"><i className="bi bi-key me-2"></i>Générer des codes manuels</h6>
          <p style={{ fontSize: ".85rem", color: "#666" }}>Les codes seront générés au format <strong>QL-ANNÉE-NUMÉRO-XXX</strong></p>
          <form onSubmit={handleGenerate}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ minWidth: 250 }}>
                <label className="form-label">Manuel *</label>
                <select className="form-control" value={genManuel} onChange={e => setGenManuel(e.target.value)} required>
                  <option value="">-- Choisir un manuel --</option>
                  {manuels.map(m => <option key={m.id} value={m.id}>{m.title} — {m.level}</option>)}
                </select>
              </div>
              <div style={{ width: 130 }}>
                <label className="form-label">Quantité *</label>
                <input type="number" className="form-control" min={1} max={500} value={genQty} onChange={e => setGenQty(e.target.value)} required />
              </div>
              <button type="submit" className="btn btn-success btn-sm" disabled={genLoading}>
                {genLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-magic me-1"></i>}
                Générer
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowGen(false)}>Fermer</button>
            </div>
          </form>
          {genError && <div className="alert alert-danger mt-3 py-2">{genError}</div>}
          {genResult && (
            <div className="alert alert-success mt-3 py-2">
              <strong>{genResult.total} codes générés avec succès !</strong>
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {genResult.codes.slice(0, 20).map(c => <span key={c.id} style={{ background: "#d1fae5", color: "#065f46", borderRadius: 6, padding: "2px 10px", fontSize: ".8rem", fontFamily: "monospace" }}>{c.code}</span>)}
                {genResult.codes.length > 20 && <span style={{ color: "#666", fontSize: ".8rem" }}>…et {genResult.codes.length - 20} autres</span>}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="ap-filter-bar mb-3">
        <div className="ap-filter-search">
          <i className="bi bi-search"></i>
          <input
            className="ap-filter-input"
            placeholder="Rechercher code, élève, manuel, école…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        {search && (
          <button className="btn btn-outline-secondary btn-sm" onClick={() => setSearch("")}>
            <i className="bi bi-x-circle me-1"></i>Effacer
          </button>
        )}
      </div>

      <div className="ap-table-wrap">
        {loading ? (
          <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        ) : (
          <table className="ap-table">
            <thead>
              <tr>
                <th>CODE</th>
                <th>MANUEL</th>
                <th>ÉLÈVE</th>
                <th>ÉCOLE</th>
                <th>CLASSE</th>
                <th>DATE D'ACTIVATION</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id}>
                  <td><span className="ap-code-badge">{c.code}</span></td>
                  <td>{c.manuel}</td>
                  <td>
                    {c.owner_nom_complet
                      ? <div>
                          <div className="ap-table-uname">{c.owner_nom_complet}</div>
                          <div className="ap-table-email">{c.owner_username}</div>
                        </div>
                      : <span className="text-muted fst-italic">Non activé</span>
                    }
                  </td>
                  <td>{c.owner_ecole || "–"}</td>
                  <td>{c.owner_classe || "–"}</td>
                  <td className="ap-table-date">{c.activated_at ? formatDate(c.activated_at) : "–"}</td>
                  <td>
                    {!c.owner_id && (
                      <button className="btn btn-outline-danger btn-sm" style={{ padding: "2px 8px" }} onClick={() => handleDeleteCode(c.id)} title="Supprimer">
                        <i className="bi bi-trash" style={{ fontSize: ".75rem" }}></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-4 text-muted">Aucun code trouvé</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-2 text-muted" style={{ fontSize: "0.8rem" }}>
        {filtered.length} code(s) affiché(s) · {codes.filter(c => c.owner_id).length} activé(s) sur {codes.length} total
      </div>
    </div>
  );
}

// ── Content list pages (Modules / Leçons / Vidéos / QCM / Exercices) ──
const CONTENT_CONFIG = {
  modules:   { title: "Modules",   icon: "bi-layers",        color: "#0d6efd", api: "modules/"   },
  lecons:    { title: "Leçons",    icon: "bi-file-text",     color: "#0d6efd", api: "lecons/"    },
  videos:    { title: "Vidéos",    icon: "bi-play-circle",   color: "#198754", api: "videos/"    },
  qcm:       { title: "QCM",       icon: "bi-grid-3x3",      color: "#20c997", api: "qcms/"      },
  exercices: { title: "Exercices", icon: "bi-pencil-square", color: "#fd7e14", api: "exercices/" },
};

function ContentListPage({ type }) {
  const cfg = CONTENT_CONFIG[type] || CONTENT_CONFIG.modules;
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState("");
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    setLoading(true);
    setSearch("");
    setPlayingVideo(null);
    api.get(cfg.api)
      .then(r => setItems(r.data?.results || r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [type, cfg.api]);

  const filtered = items.filter(item =>
    (item.title || "").toLowerCase().includes(search.toLowerCase())
  );

  const renderRow = (item) => {
    if (type === "modules") return (
      <div key={item.id} className="ap-content-row">
        <div className="ap-content-icon" style={{ color: cfg.color }}><i className={`bi ${cfg.icon}`}></i></div>
        <div className="ap-content-info">
          <div className="ap-content-title">{item.title}</div>
          <div className="ap-content-desc">
            {item.manuel_title && <span className="me-3"><i className="bi bi-book me-1"></i>{item.manuel_title}</span>}
            {item.matiere_name && <span className="me-3"><i className="bi bi-tag me-1"></i>{item.matiere_name}</span>}
            <span className="me-2"><i className="bi bi-file-text me-1"></i>{item.lecons?.length ?? 0} leçon(s)</span>
            <span className="me-2"><i className="bi bi-play-circle me-1"></i>{item.video_count ?? 0} vidéo(s)</span>
            <span className="me-2"><i className="bi bi-grid-3x3 me-1"></i>{item.qcm_count ?? 0} QCM</span>
            <span><i className="bi bi-pencil-square me-1"></i>{item.exercice_count ?? 0} exercice(s)</span>
          </div>
        </div>
      </div>
    );
    if (type === "lecons") return (
      <div key={item.id} className="ap-content-row">
        <div className="ap-content-icon" style={{ color: cfg.color }}><i className={`bi ${cfg.icon}`}></i></div>
        <div className="ap-content-info">
          <div className="ap-content-title">{item.title}</div>
          <div className="ap-content-desc">
            {item.module_title && <span className="me-3"><i className="bi bi-layers me-1"></i>{item.module_title}</span>}
            {item.pdf_url && <span className="badge bg-info text-dark me-2">PDF</span>}
          </div>
        </div>
        <span className={`badge ${item.published ? "bg-success" : "bg-secondary"}`}>
          {item.published ? "Publié" : "Brouillon"}
        </span>
      </div>
    );
    if (type === "videos") return (
      <APVideoCard
        key={item.id}
        video={item}
        playing={playingVideo === item.id}
        onPlay={() => setPlayingVideo(item.id)}
      />
    );
    if (type === "qcm") return (
      <div key={item.id} className="ap-content-row">
        <div className="ap-content-icon" style={{ color: cfg.color }}><i className={`bi ${cfg.icon}`}></i></div>
        <div className="ap-content-info">
          <div className="ap-content-title">{item.title}</div>
          <div className="ap-content-desc">
            {item.module_title && <span className="me-3"><i className="bi bi-layers me-1"></i>{item.module_title}</span>}
            <span><i className="bi bi-question-circle me-1"></i>{item.question_count ?? item.questions?.length ?? 0} question(s)</span>
          </div>
        </div>
        <span className={`badge ${item.published ? "bg-success" : "bg-secondary"}`}>
          {item.published ? "Publié" : "Brouillon"}
        </span>
      </div>
    );
    /* exercices */
    return (
      <div key={item.id} className="ap-content-row">
        <div className="ap-content-icon" style={{ color: cfg.color }}><i className={`bi ${cfg.icon}`}></i></div>
        <div className="ap-content-info">
          <div className="ap-content-title">{item.title}</div>
          <div className="ap-content-desc">
            {item.module_title && <span className="me-3"><i className="bi bi-layers me-1"></i>{item.module_title}</span>}
            {item.pedagogical_type && <span className="badge bg-warning text-dark me-2">{item.pedagogical_type}</span>}
            {item.format && <span className="badge bg-light text-dark border">{item.format}</span>}
          </div>
        </div>
        <span className={`badge ${item.published ? "bg-success" : "bg-secondary"}`}>
          {item.published ? "Publié" : "Brouillon"}
        </span>
      </div>
    );
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <h1 className="ap-page-title">
          <i className={`bi ${cfg.icon} me-2`} style={{ color: cfg.color }}></i>{cfg.title}
        </h1>
        <span className="badge bg-secondary">{items.length} élément(s)</span>
      </div>
      <div className="ap-toolbar mb-3">
        <input
          type="text"
          className="ap-search"
          placeholder={`Rechercher un(e) ${cfg.title.toLowerCase().replace(/s$/, "")}…`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      {loading
        ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
        : (
          <div className={type === "videos" ? "ap-video-grid" : "ap-content-list"}>
            {filtered.map(item => renderRow(item))}
            {filtered.length === 0 && <p className="text-muted py-3">Aucun élément trouvé.</p>}
          </div>
        )
      }
    </div>
  );
}

// ── Placeholder pages ──────────────────────────────
function PlaceholderPage({ title }) {
  return (
    <div className="ap-page">
      <div className="ap-page-header"><h1 className="ap-page-title">{title}</h1></div>
      <div className="ap-placeholder">
        <i className="bi bi-tools"></i>
        <p>Cette section est en cours de développement.</p>
        <p className="text-muted small">Gérez ce contenu depuis l'interface d'administration Django.</p>
        <a href="/admin/" target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary mt-2">
          <i className="bi bi-gear me-2"></i>Ouvrir l'admin Django
        </a>
      </div>
    </div>
  );
}

// ── Groupes Page ──────────────────────────────────
function GroupesPage() {
  const [groupes,  setGroupes]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("admin/groupes/")
      .then(r => { console.log("admin/groupes response:", r.data); setGroupes(r.data); })
      .catch(e => console.error("admin/groupes error:", e?.response?.status, e?.response?.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Accueil</span><i className="bi bi-chevron-right"></i><span>Groupes</span></div>
          <h1 className="ap-page-title">Groupes</h1>
          <p className="ap-page-desc">Groupes de discussion créés dans l'interface d'administration Django.</p>
        </div>
      </div>

      {loading ? (
        <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
      ) : groupes.length === 0 ? (
        <div className="ap-grp-empty">
          <i className="bi bi-diagram-3" style={{ fontSize: "3rem", color: "#cbd5e1" }}></i>
          <p className="text-muted mt-3">Aucun groupe. Créez-en depuis l'interface Django Admin.</p>
        </div>
      ) : (
        <div className="ap-grp-grid">
          {groupes.map(g => (
            <div key={g.id} className={`ap-grp-card${selected?.id === g.id ? " selected" : ""}`} onClick={() => setSelected(s => s?.id === g.id ? null : g)}>
              <div className="ap-grp-card-cover">
                {g.photo_url
                  ? <img src={g.photo_url} alt={g.name} className="ap-grp-card-img" />
                  : <div className="ap-grp-card-placeholder"><i className="bi bi-people-fill"></i></div>}
              </div>
              <div className="ap-grp-card-body">
                <div className="ap-grp-card-name">{g.name}</div>
                <div className="ap-grp-card-classe"><i className="bi bi-mortarboard me-1"></i>{g.classe}</div>
                {g.sujet && <div className="ap-grp-card-sujet">{g.sujet}</div>}
                <div className="ap-grp-card-meta">
                  <span className="ap-grp-card-membres">
                    <i className="bi bi-people me-1"></i>{g.nb_membres}/{g.max_membres}
                  </span>
                  {g.teacher_name && (
                    <span className="ap-grp-card-teacher">
                      {g.teacher_photo
                        ? <img src={g.teacher_photo} alt="" style={{ width: 18, height: 18, borderRadius: "50%", marginRight: 4, objectFit: "cover" }} />
                        : <i className="bi bi-person-circle me-1"></i>}
                      <span style={{ color: "#94a3b8", marginRight: 3 }}>Professeur :</span>{g.teacher_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Détail du groupe sélectionné */}
      {selected && (
        <div className="ap-grp-detail">
          <div className="ap-grp-detail-header">
            <h3 className="ap-grp-detail-title">{selected.name}</h3>
            <button className="ap-modal-close" onClick={() => setSelected(null)}><i className="bi bi-x-lg"></i></button>
          </div>
          <div className="ap-grp-detail-body">
            {selected.sujet && <p style={{ color: "#0d6efd", fontWeight: 500, marginBottom: 12 }}><i className="bi bi-chat-quote me-1"></i>{selected.sujet}</p>}
            <p style={{ fontSize: "0.85rem", color: "#64748b", marginBottom: 16 }}>
              {selected.nb_membres} membre(s) actif(s) sur {selected.max_membres} max — Classe : {selected.classe}
            </p>
            <div className="ap-grp-detail-members">
              {selected.membres?.map(m => (
                <div key={m.user_id} className="ap-grp-detail-member-row">
                  {m.photo_url
                    ? <img src={m.photo_url} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} />
                    : <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#1d4ed8" }}>
                        {(m.nom || "?")[0].toUpperCase()}
                      </div>
                  }
                  <span className="ms-2" style={{ fontSize: "0.85rem", fontWeight: 500 }}>{m.nom}</span>
                  <span className={`ql-membre-statut ql-membre-statut--${m.statut} ms-auto`}>{m.statut}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Diagnostic e-mail (superadmin) ───────────────────────────────────────────
function DiagnosticEmailPage() {
  const [email, setEmail] = useState("");
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState("");
  const [enCours, setEnCours] = useState(false);

  const tester = async (e) => {
    e.preventDefault();
    setEnCours(true); setErreur(""); setResultat(null);
    try {
      const r = await api.post("admin/diagnostic-email/", { email });
      setResultat(r.data);
    } catch (ex) {
      setErreur(ex.response?.data?.detail || "Le diagnostic a échoué.");
    } finally {
      setEnCours(false);
    }
  };

  const ligne = (ok, texte) => (
    <li className="mb-2"><i className={`bi ${ok ? "bi-check-circle-fill text-success" : "bi-x-circle-fill text-danger"} me-2`} />{texte}</li>
  );

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 760 }}>
      <h4 className="fw-bold mb-1"><i className="bi bi-envelope-check me-2 text-primary" />Diagnostic e-mail</h4>
      <p className="text-muted">Vérifie pourquoi un e-mail (mot de passe oublié…) n'arrive pas : comptes liés à l'adresse, configuration Brevo, puis envoi d'un e-mail de test.</p>
      <form className="d-flex gap-2 mb-3 flex-wrap" onSubmit={tester}>
        <input type="email" required className="form-control" style={{ maxWidth: 380 }} placeholder="adresse@exemple.com"
               value={email} onChange={e => setEmail(e.target.value)} />
        <button className="btn btn-primary" disabled={enCours}>{enCours ? "Test en cours…" : "Tester l'envoi"}</button>
      </form>
      {erreur && <div className="alert alert-danger">{erreur}</div>}
      {resultat && (
        <ul className="list-unstyled border rounded p-3 bg-white">
          {ligne(resultat.comptes_actifs > 0, resultat.comptes.length
            ? `Comptes liés à cette adresse : ${resultat.comptes.map(c => `${c.identifiant}${c.actif ? "" : " (désactivé)"}`).join(", ")}`
            : "Aucun compte n'utilise cette adresse : « mot de passe oublié » n'envoie rien (sans le dire, volontairement).")}
          {ligne(!resultat.limite_atteinte, resultat.limite_atteinte
            ? "Limite atteinte : 5 demandes de réinitialisation par heure pour cette adresse. Réessayer plus tard."
            : "Limite de tentatives non atteinte.")}
          {ligne(resultat.brevo_configure, resultat.brevo_configure
            ? `Clé Brevo configurée (expéditeur : ${resultat.expediteur}).`
            : "Clé Brevo absente : ajouter BREVO_API_KEY dans les variables Railway.")}
          {ligne(resultat.envoye, resultat.envoye
            ? "Brevo a accepté l'e-mail de test. S'il n'arrive pas, regarder les spams puis Brevo → Transactionnel → Logs."
            : `Brevo a refusé l'e-mail de test : ${resultat.raison}`)}
        </ul>
      )}
    </div>
  );
}

function AccessDeniedPage() {
  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <h1 className="ap-page-title">Accès refusé</h1>
      </div>
      <div className="ap-access-denied">
        <div className="ap-access-denied-icon">
          <i className="bi bi-shield-lock-fill"></i>
        </div>
        <h2 className="ap-access-denied-title">Accès refusé</h2>
        <p className="ap-access-denied-msg">
          Accès refusé, car vous n'avez pas le niveau d'habilitation requis.
        </p>
        <p className="ap-access-denied-sub">
          Cette section est réservée au super administrateur. Contactez votre administrateur système si vous pensez que c'est une erreur.
        </p>
      </div>
    </div>
  );
}

// ── Comptabilité : voir compta/ComptaEspace.js ─────

// ── Prix des Manuels Page ──────────────────────────
function PrixManuelsPage() {
  const [manuels, setManuels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prixMap, setPrixMap] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved]   = useState({});

  useEffect(() => {
    api.get("manuels/").then(r => {
      const list = r.data?.results || r.data;
      setManuels(list);
      setPrixMap(Object.fromEntries(list.map(m => [m.id, m.prix || 0])));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (manuelId) => {
    setSaving(s => ({ ...s, [manuelId]: true }));
    setSaved(s => ({ ...s, [manuelId]: false }));
    try {
      await api.patch(`manuels/${manuelId}/`, { prix: prixMap[manuelId] || 0 });
      setSaved(s => ({ ...s, [manuelId]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [manuelId]: false })), 2000);
    } catch {}
    setSaving(s => ({ ...s, [manuelId]: false }));
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Administration</span><i className="bi bi-chevron-right"></i><span>Prix des manuels</span></div>
          <h1 className="ap-page-title"><i className="bi bi-tag me-2"></i>Prix des manuels</h1>
          <p className="ap-page-desc">Définissez le prix de vente de chaque manuel. Ce montant sera automatiquement enregistré comme revenu lors de l'activation d'un code.</p>
        </div>
      </div>
      {loading ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div> : (
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,.08)" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {manuels.map(m => (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", paddingBottom: 14, borderBottom: "1px solid #f1f5f9" }}>
                {m.image_url
                  ? <img src={m.image_url} alt={m.title} style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover" }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}><i className="bi bi-book text-primary"></i></div>
                }
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontWeight: 700 }}>{m.title}</div>
                  <div style={{ fontSize: ".8rem", color: "#888" }}>{m.level}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div className="input-group input-group-sm" style={{ width: 170 }}>
                    <input
                      type="number"
                      className="form-control"
                      min={0}
                      value={prixMap[m.id] ?? 0}
                      onChange={e => setPrixMap(p => ({ ...p, [m.id]: parseInt(e.target.value) || 0 }))}
                      placeholder="0"
                    />
                    <span className="input-group-text">FCFA</span>
                  </div>
                  <button
                    className={`btn btn-sm ${saved[m.id] ? "btn-success" : "btn-primary"}`}
                    disabled={saving[m.id]}
                    onClick={() => handleSave(m.id)}
                    style={{ minWidth: 42 }}
                  >
                    {saving[m.id]
                      ? <span className="spinner-border spinner-border-sm"></span>
                      : saved[m.id]
                        ? <i className="bi bi-check-lg"></i>
                        : <i className="bi bi-floppy"></i>
                    }
                  </button>
                  {(prixMap[m.id] || 0) > 0 && (
                    <span style={{ fontSize: ".8rem", color: "#22c55e", fontWeight: 700, minWidth: 80 }}>
                      {new Intl.NumberFormat("fr-FR").format(prixMap[m.id])} FCFA
                    </span>
                  )}
                </div>
              </div>
            ))}
            {manuels.length === 0 && <p className="text-muted">Aucun manuel trouvé.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Codes Comptables Page ──────────────────────────
const CODES_PERSONNEL = {
  comptable: {
    endpoint: "admin/codes/comptable/", titre: "Codes Comptables", prefixe: "QL-COMP", icone: "bi-person-badge",
    role: "comptable", colonne: "COMPTABLE",
    description: "Codes d'accès réservés au comptable — format QL-COMP-ANNÉE-NNNN-XXX. Séparés des codes manuels élèves.",
  },
  community: {
    endpoint: "admin/codes/community/", titre: "Codes Community Manager", prefixe: "QL-COMM", icone: "bi-megaphone",
    role: "Community Manager", colonne: "COMMUNITY MANAGER",
    description: "Code à transmettre à la Community Manager : elle le saisit une seule fois, à son inscription, à la place du code manuel. Ensuite elle se connecte sans code.",
  },
  secretariat: {
    endpoint: "admin/codes/secretariat/", titre: "Codes Secrétariat", prefixe: "QL-SEC", icone: "bi-person-workspace",
    role: "secrétariat", colonne: "SECRÉTARIAT",
    description: "Code à transmettre au secrétariat : il est saisi une seule fois, à l'inscription, à la place du code manuel. Ensuite la connexion se fait sans code.",
  },
  ambassadeur: {
    endpoint: "admin/codes/ambassadeur/", titre: "Codes Brand Ambassadors", prefixe: "QL-AMB", icone: "bi-shop",
    role: "Brand Ambassador", colonne: "BRAND AMBASSADOR",
    description: "Code à transmettre à un Brand Ambassador : il le saisit une seule fois, à l'inscription, à la place du code manuel. Ensuite la connexion se fait sans code.",
  },
};

function CodesComptablePage({ type = "comptable" }) {
  const cfg = CODES_PERSONNEL[type];
  const [codes, setCodes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showGen, setShowGen]     = useState(false);
  const [genQty, setGenQty]       = useState(1);
  const [genNote, setGenNote]     = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult]   = useState(null);
  const [genError, setGenError]     = useState("");

  const load = () => {
    setLoading(true);
    api.get(cfg.endpoint).then(r => setCodes(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps -- une page par type (clé React)

  const handleGenerate = async (e) => {
    e.preventDefault();
    setGenError(""); setGenResult(null); setGenLoading(true);
    try {
      const r = await api.post(cfg.endpoint, { quantite: genQty, note: genNote });
      setGenResult(r.data);
      load();
    } catch(err) {
      setGenError(err?.response?.data?.detail || "Erreur lors de la génération.");
    } finally { setGenLoading(false); }
  };

  const handleDelete = (id) => {
    qlConfirm(`Supprimer ce code ${cfg.role} ?`, async () => {
      try {
        await api.delete(cfg.endpoint, { data: { code_id: id } });
        load();
      } catch(err) { qlAlert(err?.response?.data?.detail || "Erreur.", "error"); }
    }, { danger: true, okLabel: "Supprimer" });
  };

  const activated = codes.filter(c => c.owner_id).length;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Administration</span><i className="bi bi-chevron-right"></i><span>{cfg.titre}</span></div>
          <h1 className="ap-page-title"><i className={`bi ${cfg.icone} me-2`}></i>{cfg.titre}</h1>
          <p className="ap-page-desc">{cfg.description}</p>
        </div>
        <button className="btn btn-success btn-sm" onClick={() => { setShowGen(!showGen); setGenResult(null); setGenError(""); }}>
          <i className="bi bi-plus-circle me-1"></i>Générer des codes
        </button>
      </div>

      {showGen && (
        <div style={{ background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,.1)", marginBottom: 20 }}>
          <h6 className="fw-bold mb-2"><i className="bi bi-key me-2 text-warning"></i>Générer des codes {cfg.role}</h6>
          <p style={{ fontSize: ".82rem", color: "#666", marginBottom: 12 }}>Format : <strong style={{ fontFamily: "monospace" }}>{cfg.prefixe}-{new Date().getFullYear()}-0001-XYZ</strong></p>
          <form onSubmit={handleGenerate}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
              <div style={{ width: 120 }}>
                <label className="form-label">Quantité</label>
                <input type="number" className="form-control" min={1} max={type === "comptable" ? 100 : 20} value={genQty} onChange={e => setGenQty(e.target.value)} required />
              </div>
              <div style={{ minWidth: 200 }}>
                <label className="form-label">Note (optionnel)</label>
                <input type="text" className="form-control" placeholder={`Ex : Pour la ${cfg.role}`} value={genNote} onChange={e => setGenNote(e.target.value)} />
              </div>
              <button type="submit" className="btn btn-success btn-sm" disabled={genLoading}>
                {genLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-magic me-1"></i>}
                Générer
              </button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowGen(false)}>Fermer</button>
            </div>
          </form>
          {genError && <div className="alert alert-danger mt-3 py-2">{genError}</div>}
          {genResult && (
            <div className="alert alert-success mt-3 py-2">
              <strong>{genResult.total} code(s) généré(s) !</strong>
              <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {genResult.codes.map(c => (
                  <span key={c.id} style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b", borderRadius: 6, padding: "3px 12px", fontSize: ".85rem", fontFamily: "monospace", fontWeight: 700 }}>{c.code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-2 mb-3 text-muted" style={{ fontSize: ".8rem" }}>
        {activated} activé(s) · {codes.length - activated} disponible(s) · {codes.length} total
      </div>

      {loading ? <div className="ap-loading"><div className="spinner-border text-primary"></div></div> : (
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr><th>CODE</th><th>STATUT</th><th>{cfg.colonne}</th><th>DATE ACTIVATION</th><th>NOTE</th><th></th></tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id}>
                  <td>
                    <span style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #f59e0b", borderRadius: 6, padding: "2px 10px", fontFamily: "monospace", fontWeight: 700, fontSize: ".85rem" }}>
                      {c.code}
                    </span>
                  </td>
                  <td>
                    {c.owner_id
                      ? <span style={{ background: "#dcfce7", color: "#16a34a", borderRadius: 6, padding: "2px 8px", fontSize: ".75rem", fontWeight: 600 }}><i className="bi bi-check-circle-fill me-1"></i>Activé</span>
                      : <span style={{ background: "#fef3c7", color: "#92400e", borderRadius: 6, padding: "2px 8px", fontSize: ".75rem", fontWeight: 600 }}><i className="bi bi-hourglass me-1"></i>Disponible</span>
                    }
                  </td>
                  <td>{c.owner_nom || <span className="text-muted fst-italic">—</span>}</td>
                  <td style={{ fontSize: ".8rem" }}>{c.activated_at ? new Date(c.activated_at).toLocaleDateString("fr-FR") : "—"}</td>
                  <td style={{ fontSize: ".8rem", color: "#888" }}>{c.note || "—"}</td>
                  <td>
                    {!c.owner_id && (
                      <button className="btn btn-outline-danger btn-sm" style={{ padding: "2px 8px" }} onClick={() => handleDelete(c.id)}>
                        <i className="bi bi-trash" style={{ fontSize: ".75rem" }}></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-muted">Aucun code {cfg.role}. Cliquez sur "Générer des codes" pour en créer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Journal d'Audit ────────────────────────────────
function AuditLogPage({ initialUser = "" }) {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState(initialUser);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo]     = useState("");
  const [source, setSource]     = useState("");
  const [role, setRole]         = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  const buildParams = () => {
    const p = new URLSearchParams();
    if (search)   p.set("user", search);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo)   p.set("date_to", dateTo);
    if (source)   p.set("source", source);
    if (role)     p.set("role", role);
    return p.toString();
  };

  const load = () => {
    setLoading(true);
    api.get(`admin/audit/?${buildParams()}`)
      .then(r => setLogs(r.data.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  // Relancer si on arrive avec un utilisateur pré-sélectionné (bouton cadenas)
  useEffect(() => { if (initialUser) load(); }, [initialUser]); // eslint-disable-line

  const handlePdf = (imprimer = false) => {
    setPdfLoading(true);
    api.get(`admin/audit/pdf/?${buildParams()}`, { responseType: "arraybuffer" })
      .then(r => {
        const blob = new Blob([r.data], { type: "application/pdf" });
        const name = initialUser ? `audit_${initialUser}.pdf` : `audit_${dateFrom || "debut"}_${dateTo || "fin"}.pdf`;
        return imprimer ? pdfPrint(blob) : pdfOpen(blob, name);
      })
      .catch(() => qlAlert("Erreur lors de la génération PDF.", "error"))
      .finally(() => setPdfLoading(false));
  };

  const SOURCE_COLORS = { "Plateforme": "#1e3a5f", "Django Admin": "#7c3aed" };
  const fmtDt = iso => {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR") + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <h1 className="ap-page-title"><i className="bi bi-shield-lock me-2"></i>Journal d'Audit{initialUser ? ` — @${initialUser}` : ""}</h1>
        <p className="ap-page-sub">{initialUser ? `Activité de @${initialUser}` : "Toutes les actions enregistrées sur la plateforme"} — {logs.length} entrées</p>
      </div>

      {/* Filtres */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-end">
        <div>
          <label className="form-label form-label-sm mb-1">Utilisateur</label>
          <input type="text" className="form-control form-control-sm" placeholder="Nom ou @username" value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            style={{ width: 200 }} />
        </div>
        <div>
          <label className="form-label form-label-sm mb-1">Du</label>
          <input type="date" className="form-control form-control-sm" value={dateFrom}
            onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="form-label form-label-sm mb-1">Au</label>
          <input type="date" className="form-control form-control-sm" value={dateTo}
            onChange={e => setDateTo(e.target.value)} />
        </div>
        <div>
          <label className="form-label form-label-sm mb-1">Source</label>
          <select className="form-select form-select-sm" value={source} onChange={e => setSource(e.target.value)} style={{ width: 160 }}>
            <option value="">Toutes les sources</option>
            <option value="Plateforme">Plateforme</option>
            <option value="Django Admin">Django Admin</option>
          </select>
        </div>
        <div>
          <label className="form-label form-label-sm mb-1">Fonction</label>
          <select className="form-select form-select-sm" value={role} onChange={e => setRole(e.target.value)} style={{ width: 190 }}>
            <option value="">Toutes les fonctions</option>
            <option value="secretariat">Secrétariat</option>
            <option value="brand_ambassador">Brand Ambassadors</option>
            <option value="comptable">Comptable</option>
            <option value="community_manager">Community Manager</option>
            <option value="admin">Administrateurs</option>
            <option value="enseignant">Enseignants</option>
            <option value="parent">Parents</option>
            <option value="eleve">Élèves</option>
          </select>
        </div>
        <button className="btn btn-primary btn-sm" onClick={load}><i className="bi bi-search me-1"></i>Filtrer</button>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => { setSearch(""); setDateFrom(""); setDateTo(""); setSource(""); setRole(""); setTimeout(load, 50); }}>
          <i className="bi bi-x-circle me-1"></i>Réinitialiser
        </button>
        <button className="btn btn-danger btn-sm ms-auto" onClick={() => handlePdf(false)} disabled={pdfLoading}>
          {pdfLoading ? <span className="spinner-border spinner-border-sm me-1"></span> : <i className="bi bi-file-earmark-pdf me-1"></i>}
          Exporter PDF
        </button>
        <button className="btn btn-outline-danger btn-sm" onClick={() => handlePdf(true)} disabled={pdfLoading}>
          <i className="bi bi-printer me-1"></i>Imprimer
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : logs.length === 0 ? (
        <div className="ap-empty"><i className="bi bi-shield-check ap-empty-icon"></i><p>Aucune action enregistrée pour ces critères.</p></div>
      ) : (
        <div className="ap-table-wrap">
          <table className="ap-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Utilisateur</th>
                <th>Source</th>
                <th>Action</th>
                <th>Cible</th>
                <th>Détails</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(l => (
                <tr key={l.id}>
                  <td style={{ whiteSpace: "nowrap", fontSize: ".8rem" }}>{fmtDt(l.created_at)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{l.actor_name}</div>
                    <div style={{ fontSize: ".75rem", color: "#888" }}>@{l.actor}{l.role ? ` · ${l.role}` : ""}</div>
                  </td>
                  <td>
                    <span style={{ background: SOURCE_COLORS[l.source] || "#555", color: "#fff", borderRadius: 6, padding: "2px 8px", fontSize: ".75rem" }}>
                      {l.source}
                    </span>
                  </td>
                  <td style={{ fontWeight: 600, fontSize: ".85rem" }}>{l.action}</td>
                  <td style={{ fontSize: ".85rem" }}>{l.target || "–"}</td>
                  <td style={{ fontSize: ".8rem", color: "#666", maxWidth: 220 }}>{l.details || "–"}</td>
                  <td style={{ fontSize: ".8rem", color: "#888" }}>{l.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── AdminPanel ─────────────────────────────────────
// ── Quick Lives (page admin) ─────────────────────
function LivesAdminPage() {
  const [lives,    setLives]    = React.useState([]);
  const [manuels,  setManuels]  = React.useState([]);
  const [loading,  setLoading]  = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editing,  setEditing]  = React.useState(null);
  const [saving,   setSaving]   = React.useState(false);
  const [msg,      setMsg]      = React.useState(null);
  const emptyForm = { titre: "", description: "", youtube_url: "", manuel_id: "", scheduled_at: "", statut: "a_venir" };
  const [form, setForm] = React.useState(emptyForm);

  React.useEffect(() => {
    Promise.all([api.get("lives/"), api.get("manuels/")]).then(([lr, mr]) => {
      setLives(lr.data || []);
      setManuels(mr.data?.results || mr.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit   = (live) => {
    setEditing(live);
    const dt = live.scheduled_at ? live.scheduled_at.slice(0, 16) : "";
    setForm({
      titre: live.titre, description: live.description || "",
      youtube_url: `https://www.youtube.com/watch?v=${live.youtube_id}`,
      manuel_id: live.manuel_id || "", scheduled_at: dt, statut: live.statut,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg(null);
    try {
      if (editing) {
        await api.patch(`lives/${editing.id}/`, form);
        setLives(prev => prev.map(l => l.id === editing.id
          ? { ...l, statut: form.statut, titre: form.titre, description: form.description }
          : l));
        setMsg({ type: "success", text: "Live mis à jour." });
      } else {
        await api.post("lives/", form);
        const lr = await api.get("lives/");
        setLives(lr.data || []);
        setMsg({ type: "success", text: "Live créé." });
      }
      setShowForm(false);
    } catch (ex) {
      setMsg({ type: "danger", text: ex.response?.data?.error || "Erreur lors de l'enregistrement." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (live) => {
    qlConfirm(`Supprimer "${live.titre}" ?`, async () => {
      try {
        await api.delete(`lives/${live.id}/`);
        setLives(prev => prev.filter(l => l.id !== live.id));
      } catch {
        setMsg({ type: "danger", text: "Erreur lors de la suppression." });
      }
    }, { danger: true, okLabel: "Supprimer" });
  };

  const handleStatut = async (live, statut) => {
    try {
      await api.patch(`lives/${live.id}/`, { statut });
      setLives(prev => prev.map(l => l.id === live.id ? { ...l, statut } : l));
    } catch {
      setMsg({ type: "danger", text: "Erreur mise à jour statut." });
    }
  };

  const STATUT_LABELS = { a_venir: { label: "À venir", cls: "secondary" }, en_cours: { label: "EN COURS 🔴", cls: "danger" }, termine: { label: "Terminé", cls: "success" } };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="fw-bold mb-0"><i className="bi bi-broadcast me-2 text-danger"></i>Quick Lives</h4>
        <button className="btn btn-danger btn-sm" onClick={openCreate}><i className="bi bi-plus-lg me-1"></i>Nouveau Live</button>
      </div>
      {msg && <div className={`alert alert-${msg.type} alert-dismissible`}>{msg.text}<button className="btn-close" onClick={() => setMsg(null)} /></div>}

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="fw-bold mb-3">{editing ? "Modifier le live" : "Créer un live"}</h6>
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Titre *</label>
                  <input className="form-control" value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} required />
                </div>
                <div className="col-md-8">
                  <label className="form-label">URL YouTube *</label>
                  <input className="form-control" placeholder="https://youtube.com/live/..." value={form.youtube_url} onChange={e => setForm(f => ({ ...f, youtube_url: e.target.value }))} required />
                  <div className="form-text">Accepte les liens YouTube Watch, Live et Shorts.</div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Statut</label>
                  <select className="form-select" value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                    <option value="a_venir">À venir</option>
                    <option value="en_cours">En cours</option>
                    <option value="termine">Terminé</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Manuel concerné *</label>
                  <select className="form-select" value={form.manuel_id} onChange={e => setForm(f => ({ ...f, manuel_id: e.target.value }))} required>
                    <option value="">— Sélectionner un manuel —</option>
                    {manuels.map(m => <option key={m.id} value={m.id}>{m.title} — {m.level}</option>)}
                  </select>
                  <div className="form-text">Seuls les élèves ayant activé un code de ce manuel auront accès.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Date et heure *</label>
                  <input type="datetime-local" className="form-control" value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} required />
                </div>
                <div className="col-12">
                  <label className="form-label">Description</label>
                  <textarea className="form-control" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>
              </div>
              <div className="d-flex gap-2 mt-3">
                <button className="btn btn-danger btn-sm" type="submit" disabled={saving}>{saving ? "Enregistrement…" : (editing ? "Enregistrer" : "Créer")}</button>
                <button className="btn btn-outline-secondary btn-sm" type="button" onClick={() => setShowForm(false)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-4"><div className="spinner-border text-danger" /></div> : (
        lives.length === 0 ? (
          <div className="text-center text-muted py-5"><i className="bi bi-broadcast fs-1"></i><p className="mt-2">Aucun live programmé.</p></div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>Titre</th><th>Manuel</th><th>Date</th><th>Statut</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {lives.map(live => {
                  const s = STATUT_LABELS[live.statut] || { label: live.statut, cls: "secondary" };
                  return (
                    <tr key={live.id}>
                      <td>
                        <div className="fw-semibold">{live.titre}</div>
                        <small className="text-muted font-monospace">{live.youtube_id}</small>
                      </td>
                      <td><small>{live.manuel_titre || "—"}</small></td>
                      <td><small>{live.scheduled_at ? new Date(live.scheduled_at).toLocaleString("fr-FR") : "—"}</small></td>
                      <td>
                        <select className={`form-select form-select-sm border-${s.cls} text-${s.cls}`} style={{ width: 130 }} value={live.statut} onChange={e => handleStatut(live, e.target.value)}>
                          <option value="a_venir">À venir</option>
                          <option value="en_cours">En cours 🔴</option>
                          <option value="termine">Terminé</option>
                        </select>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(live)} title="Modifier"><i className="bi bi-pencil"></i></button>
                          <a className="btn btn-outline-secondary btn-sm" href={`https://www.youtube.com/watch?v=${live.youtube_id}`} target="_blank" rel="noreferrer" title="Voir sur YouTube"><i className="bi bi-youtube"></i></a>
                          <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(live)} title="Supprimer"><i className="bi bi-trash"></i></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

// ── Formules utiles (page admin) ──────────────────
function FormulesAdminPage() {
  const [formules, setFormules]   = useState([]);
  const [modules,  setModules]    = useState([]);
  const [manuels,  setManuels]    = useState([]);
  const [manuelFilter, setManuelFilter] = useState("");
  const [loading,  setLoading]    = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing,  setEditing]    = useState(null); // formule en édition
  const [saving,   setSaving]     = useState(false);
  const [saveError, setSaveError] = useState("");
  const [deleting, setDeleting]   = useState(null);
  const emptyForm = { titre: "", contenu: "", commentaire: "", video_url: "", manuel: "", module: "", is_active: true, image: null, pdf_file: null };
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("formules/"),
      api.get("modules/"),
      api.get("manuels/"),
    ]).then(([fr, mr, mar]) => {
      setFormules(fr.data);
      setModules(mr.data);
      setManuels(mar.data?.results || mar.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const manuelLabel = (m) => m.level ? `${m.title} — ${m.level}` : m.title;
  const formModules = modules.filter(m => String(m.manuel) === String(form.manuel));
  const visibleFormules = manuelFilter === "none"
    ? formules.filter(f => !f.manuel)
    : manuelFilter ? formules.filter(f => String(f.manuel) === manuelFilter) : formules;

  const openCreate = () => { setEditing(null); setSaveError(""); setForm({ ...emptyForm, manuel: manuelFilter && manuelFilter !== "none" ? manuelFilter : "" }); setShowForm(true); };
  const openEdit   = (f) => {
    setEditing(f);
    setSaveError("");
    setForm({ titre: f.titre, contenu: f.contenu, commentaire: f.commentaire, video_url: f.video_url || "", manuel: f.manuel || "", module: f.module || "", is_active: f.is_active, image: null, pdf_file: null });
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSave = async () => {
    if (!form.titre.trim() || !form.contenu.trim() || !form.manuel) return;
    setSaving(true);
    setSaveError("");
    const fd = new FormData();
    fd.append("titre",       form.titre);
    fd.append("contenu",     form.contenu);
    fd.append("commentaire", form.commentaire);
    fd.append("video_url",   form.video_url);
    fd.append("manuel",      form.manuel);
    fd.append("module",      form.module || "");
    fd.append("is_active",   form.is_active);
    if (form.image)    fd.append("image",    form.image);
    if (form.pdf_file) fd.append("pdf_file", form.pdf_file);
    try {
      if (editing) {
        await api.patch(`formules/${editing.id}/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("formules/", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      closeForm();
      load();
    } catch(e) {
      const data = e.response?.data;
      setSaveError((data && Object.values(data).flat().join(" ")) || "Enregistrement impossible.");
    }
    setSaving(false);
  };

  const handleDelete = (id) => {
    qlConfirm("Supprimer cette formule ?", async () => {
      setDeleting(id);
      await api.delete(`formules/${id}/`).catch(() => {});
      setDeleting(null);
      load();
    }, { danger: true, okLabel: "Supprimer" });
  };

  const toggleActive = async (f) => {
    await api.patch(`formules/${f.id}/`, { is_active: !f.is_active }).catch(() => {});
    load();
  };

  const ytId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return m ? m[1] : null;
  };

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <div>
          <div className="ap-breadcrumb"><span>Contenu pédagogique</span><i className="bi bi-chevron-right"></i><span>Formules utiles</span></div>
          <h1 className="ap-page-title">Formules utiles</h1>
          <p className="ap-page-desc">Chaque formule est rattachée à un manuel : seuls les élèves de ce manuel la voient.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select className="form-select form-select-sm" style={{ width: "auto" }} value={manuelFilter} onChange={e => setManuelFilter(e.target.value)}>
            <option value="">Tous les manuels</option>
            {manuels.map(m => <option key={m.id} value={String(m.id)}>{manuelLabel(m)}</option>)}
            <option value="none">Sans manuel (invisibles)</option>
          </select>
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1"></i>Ajouter une formule
          </button>
        </div>
      </div>

      {/* ── Modale create/edit ── */}
      {showForm && (
        <div className="ap-modal-overlay" onClick={e => { if (e.target === e.currentTarget) closeForm(); }}>
          <div className="ap-modal" style={{ maxWidth: 600, width: "100%" }}>
            <div className="ap-modal-header">
              <span className="ap-modal-title">{editing ? "Modifier la formule" : "Nouvelle formule"}</span>
              <button className="ap-modal-close" onClick={closeForm}><i className="bi bi-x-lg"></i></button>
            </div>
            <div className="ap-modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="form-label fw-semibold">Titre *</label>
                <input className="form-control" value={form.titre} onChange={e => setForm(f => ({ ...f, titre: e.target.value }))} placeholder="Ex: Loi d'Ohm" />
              </div>
              <div>
                <label className="form-label fw-semibold">Formule / Contenu * <small className="text-muted">(LaTeX ou texte)</small></label>
                <textarea className="form-control" rows={3} value={form.contenu} onChange={e => setForm(f => ({ ...f, contenu: e.target.value }))} placeholder="Ex: U = R × I  ou  $U = R \cdot I$" />
              </div>
              <div>
                <label className="form-label fw-semibold">Commentaire explicatif</label>
                <textarea className="form-control" rows={3} value={form.commentaire} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))} placeholder="Explication, conditions d'application…" />
              </div>
              <div>
                <label className="form-label fw-semibold">Manuel *</label>
                <select className="form-select" value={form.manuel} onChange={e => setForm(f => ({ ...f, manuel: e.target.value, module: "" }))}>
                  <option value="">— Choisir un manuel —</option>
                  {manuels.map(m => <option key={m.id} value={m.id}>{manuelLabel(m)}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label fw-semibold">Module associé</label>
                <select className="form-select" value={form.module} disabled={!form.manuel} onChange={e => setForm(f => ({ ...f, module: e.target.value }))}>
                  <option value="">— Aucun module (formule générale du manuel) —</option>
                  {formModules.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label fw-semibold">URL Vidéo YouTube</label>
                <input className="form-control" value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label className="form-label fw-semibold">Image illustrative</label>
                  <input type="file" className="form-control" accept="image/*" onChange={e => setForm(f => ({ ...f, image: e.target.files[0] || null }))} />
                  {editing?.image_url && !form.image && <img src={editing.image_url} alt="" style={{ marginTop: 6, maxHeight: 60, borderRadius: 6 }} />}
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label className="form-label fw-semibold">Fichier PDF</label>
                  <input type="file" className="form-control" accept="application/pdf" onChange={e => setForm(f => ({ ...f, pdf_file: e.target.files[0] || null }))} />
                  {editing?.pdf_url && !form.pdf_file && <DownloadLink url={editing.pdf_url} filename={`${editing.titre || "formule"}.pdf`} mimeType="application/pdf" downloadOnWeb={false} target="_blank" rel="noopener noreferrer" className="d-block mt-1 small"><i className="bi bi-file-pdf me-1 text-danger"></i>PDF actuel</DownloadLink>}
                </div>
              </div>
              <div className="form-check">
                <input className="form-check-input" type="checkbox" id="fIsActive" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label className="form-check-label" htmlFor="fIsActive">Visible par les élèves</label>
              </div>
              {saveError && <div className="alert alert-danger py-2 mb-0">{saveError}</div>}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="btn btn-outline-secondary btn-sm" onClick={closeForm}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.titre.trim() || !form.contenu.trim() || !form.manuel}>
                  {saving ? <span className="spinner-border spinner-border-sm me-1"></span> : null}
                  {editing ? "Enregistrer" : "Créer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Liste des formules ── */}
      {loading ? (
        <div className="ap-loading"><div className="spinner-border text-primary"></div></div>
      ) : visibleFormules.length === 0 ? (
        <div className="ap-grp-empty">
          <i className="bi bi-infinity" style={{ fontSize: "2.5rem", color: "#94a3b8", marginBottom: 10 }}></i>
          <p className="text-muted">Aucune formule. Cliquez sur "Ajouter une formule".</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {visibleFormules.map(f => (
            <div key={f.id} className="ap-grp-detail" style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
              {/* Miniature image ou vidéo */}
              {(f.image_url || ytId(f.video_url)) && (
                <div style={{ width: 100, flexShrink: 0 }}>
                  {f.image_url
                    ? <img src={f.image_url} alt="" style={{ width: "100%", borderRadius: 8, objectFit: "cover", maxHeight: 80 }} />
                    : <img src={`https://img.youtube.com/vi/${ytId(f.video_url)}/default.jpg`} alt="" style={{ width: "100%", borderRadius: 8 }} />
                  }
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <strong style={{ fontSize: "0.97rem" }}>{f.titre}</strong>
                  {f.manuel_titre
                    ? <span className="ap-role-badge" style={{ background: "#0f766e" }}>{f.manuel_titre}</span>
                    : <span className="ap-role-badge" style={{ background: "#dc2626" }}>Sans manuel</span>}
                  {f.module_titre && <span className="ap-role-badge" style={{ background: "#6366f1" }}>{f.module_titre}</span>}
                  <span className={`ap-status-badge${f.is_active ? " active" : " inactive"}`}>{f.is_active ? "Visible" : "Masqué"}</span>
                </div>
                <div style={{ background: "#f0f9ff", padding: "8px 14px", borderRadius: 6, marginBottom: 6, borderLeft: "3px solid #0ea5e9", overflowX: "auto" }}>
                  <MathDisplay latex={f.contenu} />
                </div>
                {f.commentaire && <p style={{ fontSize: "0.83rem", color: "#64748b", margin: 0 }}>{f.commentaire}</p>}
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {f.video_url && <a href={f.video_url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-danger btn-sm"><i className="bi bi-youtube me-1"></i>Vidéo</a>}
                  {f.pdf_url   && <DownloadLink url={f.pdf_url} filename={`${f.titre || "formule"}.pdf`} mimeType="application/pdf" downloadOnWeb={false} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm"><i className="bi bi-file-pdf me-1"></i>PDF</DownloadLink>}
                  <button className="btn btn-outline-warning btn-sm" onClick={() => toggleActive(f)}>
                    <i className={`bi ${f.is_active ? "bi-eye-slash" : "bi-eye"} me-1`}></i>{f.is_active ? "Masquer" : "Afficher"}
                  </button>
                  <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(f)}><i className="bi bi-pencil me-1"></i>Modifier</button>
                  <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(f.id)} disabled={deleting === f.id}>
                    {deleting === f.id ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-trash"></i>}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ma Signature ────────────────────────────────────────────────────────────
function MaSignaturePage() {
  const [sigUrl, setSigUrl]   = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving]   = React.useState(false);
  const [msg, setMsg]         = React.useState(null);
  const [mode, setMode]       = React.useState("draw");
  const [drawing, setDrawing] = React.useState(false);
  const [lastPos, setLastPos] = React.useState(null);
  const canvasRef = React.useRef(null);
  const fileRef   = React.useRef(null);

  React.useEffect(() => {
    api.get("signature/").then(r => { setSigUrl(r.data.signature_url); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const sx = canvas.width / rect.width, sy = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - rect.left) * sx, y: (src.clientY - rect.top) * sy };
  };
  const startDraw = (e) => { e.preventDefault(); setDrawing(true); const p = getPos(e); setLastPos(p); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.arc(p.x, p.y, 1.5, 0, Math.PI*2); ctx.fillStyle="#1e293b"; ctx.fill(); };
  const draw = (e) => { e.preventDefault(); if (!drawing || !lastPos) return; const canvas = canvasRef.current; const ctx = canvas.getContext("2d"); const p = getPos(e); ctx.beginPath(); ctx.moveTo(lastPos.x, lastPos.y); ctx.lineTo(p.x, p.y); ctx.strokeStyle="#1e293b"; ctx.lineWidth=2.5; ctx.lineCap="round"; ctx.stroke(); setLastPos(p); };
  const stopDraw = () => setDrawing(false);
  const clearCanvas = () => { const canvas = canvasRef.current; canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height); };

  const save = async (blob, filename) => {
    setSaving(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append("signature", blob, filename);
      const r = await api.post("signature/",fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSigUrl(r.data.signature_url);
      setMsg({ type: "success", text: "Signature enregistrée !" });
      clearCanvas();
    } catch { setMsg({ type: "danger", text: "Erreur lors de l'enregistrement." }); }
    setSaving(false);
  };

  const saveCanvas = () => { canvasRef.current.toBlob(b => { if (b) save(b, "signature.png"); }, "image/png"); };
  const saveFile   = () => { const f = fileRef.current?.files[0]; if (f) save(f, f.name); };

  const deleteSig = () => {
    qlConfirm("Supprimer votre signature ?", async () => {
      await api.delete("signature/");
      setSigUrl(null);
      setMsg({ type: "info", text: "Signature supprimée." });
    }, { danger: true, okLabel: "Supprimer" });
  };

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary"/></div>;

  return (
    <div className="container-fluid py-4" style={{maxWidth:680}}>
      <h4 className="fw-bold mb-1"><i className="bi bi-pen me-2 text-primary"/>Ma Signature Numérique</h4>
      <p className="text-muted small mb-4">Dessinez ou importez votre signature. Elle sera apposée sur les documents que vous signerez.</p>

      {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}

      {sigUrl && (
        <div className="card mb-4 border-success">
          <div className="card-body d-flex align-items-center gap-3">
            <div>
              <div className="text-success fw-semibold small mb-1"><i className="bi bi-check-circle me-1"/>Signature enregistrée</div>
              <img src={sigUrl} alt="signature" style={{maxHeight:70, maxWidth:240, border:"1px solid #d1fae5", background:"#fff", padding:6, borderRadius:6}}/>
            </div>
            <button className="btn btn-outline-danger btn-sm ms-auto" onClick={deleteSig}><i className="bi bi-trash"/>  Supprimer</button>
          </div>
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item"><button className={`nav-link${mode==="draw"?" active":""}`} onClick={()=>setMode("draw")}><i className="bi bi-pencil me-1"/>Dessiner</button></li>
        <li className="nav-item"><button className={`nav-link${mode==="upload"?" active":""}`} onClick={()=>setMode("upload")}><i className="bi bi-upload me-1"/>Importer une image</button></li>
      </ul>

      {mode === "draw" && (
        <div>
          <p className="text-muted small">Tracez votre signature dans le cadre ci-dessous :</p>
          <div style={{border:"2px dashed #94a3b8", borderRadius:8, background:"#fff", display:"inline-block", touchAction:"none"}}>
            <canvas ref={canvasRef} width={500} height={150} style={{display:"block", cursor:"crosshair"}}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}/>
          </div>
          <div className="mt-2 d-flex gap-2">
            <button className="btn btn-outline-secondary btn-sm" onClick={clearCanvas}><i className="bi bi-eraser"/> Effacer</button>
            <button className="btn btn-success btn-sm" onClick={saveCanvas} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-check-lg me-1"/>Enregistrer</>}
            </button>
          </div>
        </div>
      )}

      {mode === "upload" && (
        <div>
          <p className="text-muted small">Importez une image PNG/JPG de votre signature :</p>
          <input ref={fileRef} type="file" accept="image/*" className="form-control mb-2" style={{maxWidth:320}}/>
          <button className="btn btn-success btn-sm" onClick={saveFile} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-upload me-1"/>Enregistrer</>}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Boîte de dépôt / signature ───────────────────────────────────────────────
function BoiteSignaturePage({ user: localUser }) {
  const [tab, setTab]               = React.useState("recus");
  const [recus, setRecus]           = React.useState([]);
  const [envoyes, setEnvoyes]       = React.useState([]);
  const [factures, setFactures]     = React.useState([]);
  const [loading, setLoading]       = React.useState(true);
  const [membres, setMembres]       = React.useState([]);
  const [showDepose, setShowDepose]   = React.useState(false);
  const [showTraiter, setShowTraiter] = React.useState(null);
  const [showComm, setShowComm]       = React.useState(false);
  const [showFacture, setShowFacture] = React.useState(false);
  const [signingId, setSigningId] = React.useState(null);
  const [msg, setMsg]                 = React.useState(null);

  const isComptable = localUser?.is_comptable || localUser?.is_superuser;

  const load = () => {
    setLoading(true);
    api.get("signature/boite/").then(r => {
      setRecus(r.data.recus);
      setEnvoyes(r.data.envoyes);
      setLoading(false);
    }).catch(() => setLoading(false));
    if (isComptable) {
      api.get("factures/").then(r => setFactures(r.data)).catch(() => {});
    }
  };

  React.useEffect(() => {
    load();
    api.get("signature/deposer/").then(r => setMembres(r.data)).catch(()=>{});
  }, []);

  const signerEtVerser = async (doc) => {
    setSigningId(doc.id);
    try {
      const r = await api.post(`factures/doc/${doc.id}/signer/`);
      setFactures(prev => prev.map(f => f.id === doc.id
        ? { ...f, statut: r.data.statut, traite_le: r.data.traite_le,
            pdf_signe_url: r.data.pdf_signe_url, verse: r.data.verse, verse_le: r.data.verse_le }
        : f
      ));
      setMsg({ type: "success", text: "Facture versée et signée. Copie renvoyée à l'expéditeur." });
    } catch(ex) {
      let txt = "Erreur lors de la signature.";
      try { if (ex.response?.data?.error) txt = ex.response.data.error; } catch {}
      setMsg({ type: "danger", text: txt });
    } finally { setSigningId(null); }
  };

  const fmt = (n) => (parseInt(n) || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const nbEnAttente = recus.filter(d => d.statut === "en_attente").length;
  const nbNonOuverts = recus.filter(d => !d.ouvert).length;

  // Pastille rouge : le document reçu n'a pas encore été ouvert.
  const marquerOuvert = (doc) => {
    if (doc.ouvert) return;
    setRecus(prev => prev.map(d => d.id === doc.id ? { ...d, ouvert: true } : d));
    api.post(`signature/documents/${doc.id}/ouvert/`).then(rafraichirCompteurs).catch(() => {});
  };

  const statutBadge = (s) => ({
    en_attente: <span className="badge bg-warning text-dark"><i className="bi bi-clock me-1"/>En attente</span>,
    signe:      <span className="badge bg-success"><i className="bi bi-check-lg me-1"/>Signé</span>,
    refuse:     <span className="badge bg-danger"><i className="bi bi-x-lg me-1"/>Refusé</span>,
  }[s] || <span className="badge bg-secondary">{s}</span>);

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h4 className="fw-bold mb-0"><i className="bi bi-inbox me-2 text-primary"/>Boîte de signature</h4>
        <div className="d-flex gap-2">
          {(localUser?.is_superuser || localUser?.is_comptable || localUser?.is_community_manager || localUser?.is_secretaire) && (
            <button className="btn btn-success btn-sm" onClick={() => setShowComm(true)}>
              <i className="bi bi-file-text me-1"/>Rédiger un communiqué
            </button>
          )}
          {(localUser?.is_comptable || localUser?.is_superuser || localUser?.is_secretaire || localUser?.is_community_manager || localUser?.is_brand_ambassador) && (
            <button className="btn btn-warning btn-sm" onClick={() => setShowFacture(true)}>
              <i className="bi bi-receipt me-1"/>Créer une facture
            </button>
          )}
          <button className="btn btn-primary btn-sm" onClick={() => setShowDepose(true)}>
            <i className="bi bi-send me-1"/>Envoyer un document à signer
          </button>
        </div>
      </div>

      {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}<button className="btn-close float-end" onClick={()=>setMsg(null)}/></div>}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link${tab==="recus"?" active":""}`} onClick={()=>setTab("recus")}>
            <i className="bi bi-inbox me-1"/>Reçus
            {nbEnAttente > 0 && <span className="badge bg-warning text-dark ms-1" title="À signer">{nbEnAttente}</span>}
            <Pastille nombre={nbNonOuverts} />
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link${tab==="envoyes"?" active":""}`} onClick={()=>setTab("envoyes")}>
            <i className="bi bi-send me-1"/>Envoyés
          </button>
        </li>
        {isComptable && (
          <li className="nav-item">
            <button className={`nav-link${tab==="factures"?" active":""}`} onClick={()=>setTab("factures")}>
              <i className="bi bi-receipt me-1"/>Factures
              {factures.filter(f => f.statut !== "signe").length > 0 && (
                <span className="badge bg-warning text-dark ms-1">{factures.filter(f=>f.statut !== "signe").length}</span>
              )}
            </button>
          </li>
        )}
      </ul>

      {loading ? <div className="text-center py-5"><div className="spinner-border text-primary"/></div> : (
        <div>
          {tab === "recus" && (
            recus.length === 0 ? <div className="text-muted text-center py-4">Aucun document reçu.</div> :
            recus.map(doc => (
              <div key={doc.id} className="card mb-2 border-0 shadow-sm">
                <div className="card-body py-2 px-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i className="bi bi-file-earmark-pdf text-danger fs-5"/>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">
                        {!doc.ouvert && <span className="ql-point-nouveau" title="Pas encore ouvert" />}
                        {doc.titre}
                      </div>
                      <div className="text-muted small">De : {doc.deposant} · {doc.depose_le} · {doc.type_doc}</div>
                      {doc.message && <div className="text-muted small fst-italic">"{doc.message}"</div>}
                      {doc.motif_refus && <div className="text-danger small">Motif : {doc.motif_refus}</div>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {statutBadge(doc.statut)}
                      {doc.pdf_original_url && (
                        <>
                          <DownloadLink url={doc.pdf_original_url} filename={`document_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" downloadOnWeb={false} titre={doc.titre} onSuccess={() => marquerOuvert(doc)} className="btn btn-outline-primary btn-sm">
                            <i className="bi bi-eye me-1"/>Voir
                          </DownloadLink>
                          <DownloadLink url={doc.pdf_original_url} filename={`document_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" onSuccess={() => marquerOuvert(doc)} className="btn btn-outline-secondary btn-sm" title="Télécharger">
                            <i className="bi bi-download"/>
                          </DownloadLink>
                        </>
                      )}
                      {doc.pdf_signe_url && (
                        <DownloadLink url={doc.pdf_signe_url} filename={`document_signe_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" downloadOnWeb={false} titre={`${doc.titre} (signé)`} onSuccess={() => marquerOuvert(doc)} className="btn btn-success btn-sm">
                          <i className="bi bi-eye me-1"/>Signé
                        </DownloadLink>
                      )}
                      {doc.statut === "en_attente" && (
                        <button className="btn btn-primary btn-sm" onClick={() => { marquerOuvert(doc); setShowTraiter(doc); }}>
                          <i className="bi bi-pen me-1"/>Traiter
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {tab === "envoyes" && (
            envoyes.length === 0 ? <div className="text-muted text-center py-4">Aucun document envoyé.</div> :
            envoyes.map(doc => (
              <div key={doc.id} className="card mb-2 border-0 shadow-sm">
                <div className="card-body py-2 px-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <i className="bi bi-file-earmark-pdf text-danger fs-5"/>
                    <div className="flex-grow-1">
                      <div className="fw-semibold">{doc.titre}</div>
                      <div className="text-muted small">À : {doc.signataire} · {doc.depose_le} · {doc.type_doc}</div>
                      {doc.traite_le && <div className="text-muted small">Traité le : {doc.traite_le}</div>}
                      {doc.motif_refus && <div className="text-danger small">Motif refus : {doc.motif_refus}</div>}
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {statutBadge(doc.statut)}
                      {doc.pdf_signe_url && (
                        <>
                          <DownloadLink url={doc.pdf_signe_url} filename={`document_signe_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" downloadOnWeb={false} titre={`${doc.titre} (signé)`} className="btn btn-outline-success btn-sm">
                            <i className="bi bi-eye me-1"/>Voir
                          </DownloadLink>
                          <DownloadLink url={doc.pdf_signe_url} filename={`document_signe_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" className="btn btn-success btn-sm">
                            <i className="bi bi-download me-1"/>Télécharger signé
                          </DownloadLink>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {tab === "factures" && isComptable && (
            factures.length === 0
              ? <div className="text-muted text-center py-4">Aucune facture reçue.</div>
              : factures.map(f => (
                <div key={f.id} className={`card mb-2 border-0 shadow-sm border-start border-4 ${f.statut === "signe" ? "border-success" : "border-warning"}`}>
                  <div className="card-body py-2 px-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <i className="bi bi-receipt fs-5" style={{color: f.statut === "signe" ? "#16a34a" : "#d97706"}}/>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{f.numero} — {f.client_nom}</div>
                        <div className="text-muted small">
                          {f.created_at}
                          {f.deposant && ` · Par : ${f.deposant}`}
                          {f.client_classe && ` · ${f.client_classe}`}
                          {f.mode_paiement && ` · ${f.mode_paiement}`}
                        </div>
                        {f.statut === "signe" && f.traite_le && (
                          <div className="text-success small">
                            <i className="bi bi-patch-check me-1"/>
                            Versé et signé le {f.traite_le}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="fw-bold text-dark">{fmt(f.total)} F CFA</span>
                        {f.statut === "signe"
                          ? <span className="badge bg-success"><i className="bi bi-patch-check me-1"/>Validé</span>
                          : <span className="badge bg-warning text-dark"><i className="bi bi-hourglass me-1"/>En attente</span>
                        }
                        {f.pdf_url && (
                          <DownloadLink url={f.pdf_url} filename={`facture_${f.reference || f.id}.pdf`} mimeType="application/pdf" downloadOnWeb={false} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm">
                            <i className="bi bi-eye"/>
                          </DownloadLink>
                        )}
                        {f.pdf_signe_url && (
                          <DownloadLink url={f.pdf_signe_url} filename={`facture_signee_${f.reference || f.id}.pdf`} mimeType="application/pdf" className="btn btn-outline-success btn-sm">
                            <i className="bi bi-download me-1"/>Signé
                          </DownloadLink>
                        )}
                        {f.statut !== "signe" && (
                          <button
                            className="btn btn-sm btn-success"
                            disabled={signingId === f.id}
                            onClick={() => signerEtVerser(f)}
                          >
                            {signingId === f.id
                              ? <span className="spinner-border spinner-border-sm"/>
                              : <><i className="bi bi-patch-check me-1"/>Versé + Signer</>
                            }
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
          )}
        </div>
      )}

      {showDepose && <DeposerDocModal membres={membres} onClose={() => setShowDepose(false)} onSuccess={() => { setShowDepose(false); setMsg({type:"success", text:"Document envoyé ! Le signataire a été notifié."}); load(); }} />}
      {showTraiter && <TraiterDocModal doc={showTraiter} onClose={() => setShowTraiter(null)} onSuccess={() => { setShowTraiter(null); setMsg({type:"success", text:"Document traité."}); load(); }} />}
      {showComm && <CommuniqueModal onClose={() => setShowComm(false)} onSuccess={(titre) => { setShowComm(false); setMsg({type:"success", text:`Communiqué "${titre}" envoyé et téléchargé.`}); load(); }} />}
      {showFacture && <FactureModal onClose={() => setShowFacture(false)} onSuccess={(num) => { setShowFacture(false); setMsg({type:"success", text:`Facture ${num} générée et téléchargée. Copie dans votre boîte.`}); load(); }} />}
    </div>
  );
}

function FactureModal({ onClose, onSuccess }) {
  const MODES = [
    { value: "especes",      label: "Espèces" },
    { value: "momo_mtn",     label: "MTN Mobile Money" },
    { value: "orange_money", label: "Orange Money" },
    { value: "autre",        label: "Autre" },
  ];
  const [client, setClient] = React.useState({ client_nom: "", client_tel: "", client_email: "", client_classe: "" });
  const [items,  setItems]  = React.useState([{ description: "", qte: 1, prix_unitaire: "", type_item: "autre", code_manuel: "" }]);
  const [remise, setRemise] = React.useState("");
  const [mode,   setMode]   = React.useState("especes");
  const [notes,  setNotes]  = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [err,    setErr]    = React.useState("");

  const sousTotal = items.reduce((s, it) => s + (parseInt(it.qte)||0) * (parseInt(it.prix_unitaire)||0), 0);
  const remiseVal = Math.max(0, parseInt(remise) || 0);
  const total     = Math.max(0, sousTotal - remiseVal);

  const addItem    = () => setItems(p => [...p, { description: "", qte: 1, prix_unitaire: "", type_item: "autre", code_manuel: "" }]);
  const removeItem = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const updItem    = (i, f, v) => setItems(p => p.map((it, idx) => idx === i ? {...it, [f]: v} : it));

  const fmt = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const submit = async (e) => {
    e.preventDefault();
    if (!client.client_nom.trim()) return setErr("Le nom du client est requis.");
    if (items.some(it => !it.description.trim() || !it.prix_unitaire)) return setErr("Complétez toutes les lignes.");
    setSaving(true); setErr("");
    try {
      const payload = {
        ...client,
        items: items.map(it => ({
          description:   it.description,
          qte:           parseInt(it.qte) || 1,
          prix_unitaire: parseInt(it.prix_unitaire) || 0,
          type_item:     it.type_item,
          code_manuel:   it.type_item === "manuel" ? it.code_manuel : "",
        })),
        remise:        remiseVal,
        mode_paiement: mode,
        notes,
      };
      const resp = await api.post("factures/", payload, { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const num  = resp.headers?.["content-disposition"]?.match(/facture_([^.]+)\.pdf/)?.[1] || "facture";
      telechargerPuisProposerImpression(blob, `${num}.pdf`, "la facture");
      onSuccess(num);
    } catch(ex) {
      let msg = "Erreur lors de la génération.";
      try {
        const d = ex.response?.data;
        if (d instanceof ArrayBuffer) {
          const text = new TextDecoder().decode(d);
          try { msg = JSON.parse(text).error || text.slice(0, 300); } catch { msg = text.slice(0, 300) || msg; }
        } else if (d?.error) { msg = d.error; }
      } catch {}
      setErr(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="modal fade show" style={{display:"block", background:"rgba(0,0,0,.5)", zIndex:1055}}>
      <div className="modal-dialog modal-lg modal-dialog-centered" style={{maxHeight:"95vh"}}>
        <div className="modal-content" style={{maxHeight:"95vh", display:"flex", flexDirection:"column"}}>
          <div className="modal-header bg-warning flex-shrink-0">
            <h5 className="mb-0 text-dark"><i className="bi bi-receipt me-2"/>Créer une facture</h5>
            <button className="btn-close" onClick={onClose}/>
          </div>
          <form onSubmit={submit} style={{display:"flex", flexDirection:"column", flex:1, minHeight:0}}>
            <div className="modal-body" style={{overflowY:"auto", flex:1}}>

              {/* Client */}
              <h6 className="text-primary fw-bold border-bottom pb-1 mb-3"><i className="bi bi-person me-1"/>Client</h6>
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold small">Nom complet *</label>
                  <input className="form-control form-control-sm" value={client.client_nom}
                    onChange={e => setClient(c=>({...c, client_nom: e.target.value}))} required maxLength={200}/>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold small">Téléphone</label>
                  <input className="form-control form-control-sm" value={client.client_tel}
                    onChange={e => setClient(c=>({...c, client_tel: e.target.value}))} maxLength={30}/>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold small">Email</label>
                  <input className="form-control form-control-sm" type="email" value={client.client_email}
                    onChange={e => setClient(c=>({...c, client_email: e.target.value}))} maxLength={200}/>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold small">Classe (si élève)</label>
                  <input className="form-control form-control-sm" value={client.client_classe}
                    onChange={e => setClient(c=>({...c, client_classe: e.target.value}))} maxLength={100}
                    placeholder="Ex: 3ème, Terminale C..."/>
                </div>
              </div>

              {/* Lignes */}
              <h6 className="text-primary fw-bold border-bottom pb-1 mb-2"><i className="bi bi-list-ul me-1"/>Prestations</h6>
              <table className="table table-sm table-bordered mb-1">
                <thead className="table-primary">
                  <tr>
                    <th>Description</th>
                    <th style={{width:100}}>Type</th>
                    <th style={{width:50}}>Qté</th>
                    <th style={{width:120}}>Prix unit. (F)</th>
                    <th style={{width:100}} className="text-end">Montant</th>
                    <th style={{width:34}}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <React.Fragment key={i}>
                      <tr>
                        <td>
                          <input className="form-control form-control-sm border-0" value={it.description}
                            onChange={e => updItem(i, "description", e.target.value)}
                            placeholder="Ex: Manuel Math 3ème / Abonnement / Cours…" required/>
                        </td>
                        <td>
                          <select className="form-select form-select-sm border-0" value={it.type_item}
                            onChange={e => updItem(i, "type_item", e.target.value)}>
                            <option value="autre">Autre</option>
                            <option value="manuel">Manuel</option>
                            <option value="abonnement">Abonnement</option>
                            <option value="cours">Cours</option>
                          </select>
                        </td>
                        <td>
                          <input className="form-control form-control-sm border-0 text-center" type="number"
                            min="1" value={it.qte} onChange={e => updItem(i, "qte", e.target.value)}/>
                        </td>
                        <td>
                          <input className="form-control form-control-sm border-0 text-end" type="number"
                            min="0" value={it.prix_unitaire} onChange={e => updItem(i, "prix_unitaire", e.target.value)}
                            placeholder="0" required/>
                        </td>
                        <td className="text-end fw-semibold align-middle">
                          {fmt((parseInt(it.qte)||0)*(parseInt(it.prix_unitaire)||0))} F
                        </td>
                        <td className="text-center align-middle">
                          {items.length > 1 && (
                            <button type="button" className="btn btn-sm btn-outline-danger p-0 px-1"
                              onClick={() => removeItem(i)}>×</button>
                          )}
                        </td>
                      </tr>
                      {it.type_item === "manuel" && (
                        <tr style={{background:"#f8fafc"}}>
                          <td colSpan={6} className="py-1 px-2">
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-warning text-dark" style={{fontSize:"0.7rem"}}>
                                <i className="bi bi-book me-1"/>Code manuel
                              </span>
                              <input className="form-control form-control-sm" style={{maxWidth:200}}
                                value={it.code_manuel}
                                onChange={e => updItem(i, "code_manuel", e.target.value)}
                                placeholder="Ex: AK-12345"/>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              <button type="button" className="btn btn-outline-primary btn-sm mb-3" onClick={addItem}>
                <i className="bi bi-plus me-1"/>Ajouter une ligne
              </button>

              {/* Sous-total, remise, total */}
              <div className="border rounded p-2 mb-3 bg-light">
                <div className="d-flex justify-content-between small text-muted mb-1">
                  <span>Sous-total</span>
                  <span className="fw-semibold">{fmt(sousTotal)} F CFA</span>
                </div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <label className="small text-danger fw-semibold mb-0" style={{whiteSpace:"nowrap"}}>
                    <i className="bi bi-tag me-1"/>Remise (F CFA)
                  </label>
                  <input className="form-control form-control-sm text-end" type="number" min="0"
                    style={{maxWidth:140}}
                    value={remise}
                    onChange={e => setRemise(e.target.value)}
                    placeholder="0"/>
                  {remiseVal > 0 && (
                    <span className="text-danger small fw-semibold">- {fmt(remiseVal)} F</span>
                  )}
                </div>
                <div className="d-flex justify-content-between fw-bold text-success">
                  <span>{remiseVal > 0 ? "TOTAL NET" : "TOTAL"}</span>
                  <span>{fmt(total)} F CFA</span>
                </div>
              </div>

              {/* Paiement + notes */}
              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label fw-semibold small">Mode de paiement</label>
                  <select className="form-select form-select-sm" value={mode} onChange={e => setMode(e.target.value)}>
                    {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold small">Notes (optionnel)</label>
                  <input className="form-control form-control-sm" value={notes}
                    onChange={e => setNotes(e.target.value)} maxLength={300}
                    placeholder="Informations complémentaires..."/>
                </div>
              </div>

            </div>
            <div className="modal-footer flex-shrink-0 flex-column align-items-stretch gap-2">
              {err && <div className="alert alert-danger py-2 mb-0 w-100">{err}</div>}
              <div className="d-flex justify-content-end gap-2 w-100">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
                <button type="submit" className="btn btn-warning" disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-1"/>Génération…</>
                    : <><i className="bi bi-file-earmark-arrow-down me-1"/>Générer et signer ({fmt(total)} F CFA)</>
                  }
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function DeposerDocModal({ membres, onClose, onSuccess }) {
  const [form, setForm]     = React.useState({ signataire_id: "", titre: "", type_doc: "autre", message: "" });
  const [file, setFile]     = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr]       = React.useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setErr("Veuillez sélectionner un fichier PDF.");
    if (!form.signataire_id) return setErr("Veuillez choisir un signataire.");
    setSaving(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("pdf_file", file);
      Object.entries(form).forEach(([k,v]) => fd.append(k, v));
      await api.post("signature/deposer/",fd, { headers: { "Content-Type": "multipart/form-data" } });
      onSuccess();
    } catch(e) { setErr(e.response?.data?.error || "Erreur lors du dépôt."); }
    setSaving(false);
  };

  return (
    <div className="modal fade show" style={{display:"block", background:"rgba(0,0,0,.5)", zIndex:1055}}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header"><h5><i className="bi bi-send me-2"/>Envoyer un document à signer</h5><button className="btn-close" onClick={onClose}/></div>
          <form onSubmit={submit}>
            <div className="modal-body">
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="mb-3">
                <label className="form-label fw-semibold">Signataire *</label>
                <select className="form-select" value={form.signataire_id} onChange={e => setForm(f=>({...f, signataire_id: e.target.value}))} required>
                  <option value="">-- Choisir --</option>
                  {membres.map(m => <option key={m.id} value={m.id}>{m.nom} ({m.role})</option>)}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Titre du document *</label>
                <input className="form-control" value={form.titre} onChange={e => setForm(f=>({...f, titre: e.target.value}))} required maxLength={200}/>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Type</label>
                <select className="form-select" value={form.type_doc} onChange={e => setForm(f=>({...f, type_doc: e.target.value}))}>
                  <option value="autre">Autre</option>
                  <option value="bulletin_paie">Bulletin de paie</option>
                  <option value="rapport">Rapport</option>
                  <option value="audit">Journal d'audit</option>
                  <option value="devoir">Devoir</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Message (optionnel)</label>
                <textarea className="form-control" rows={2} value={form.message} onChange={e => setForm(f=>({...f, message: e.target.value}))} maxLength={500}/>
              </div>
              <div className="mb-2">
                <label className="form-label fw-semibold">Fichier PDF *</label>
                <input type="file" accept=".pdf" className="form-control" onChange={e => setFile(e.target.files[0])} required/>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <span className="spinner-border spinner-border-sm me-1"/> : <i className="bi bi-send me-1"/>}Envoyer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TraiterDocModal({ doc, onClose, onSuccess }) {
  const [action, setAction]   = React.useState("");
  const [motif, setMotif]     = React.useState("");
  const [saving, setSaving]   = React.useState(false);
  const [err, setErr]         = React.useState("");

  const submit = async () => {
    if (!action) return setErr("Choisissez une action.");
    setSaving(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("action", action);
      if (motif) fd.append("motif", motif);
      if (action === "signer") {
        const resp = await api.post(`signature/traiter/${doc.id}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "arraybuffer",
        });
        const blob = new Blob([resp.data], { type: "application/pdf" });
        telechargerPuisProposerImpression(blob, `signe_${doc.titre}.pdf`, "le document signé");
      } else {
        await api.post(`signature/traiter/${doc.id}/`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onSuccess();
    } catch(e) {
      let msg = "Erreur lors du traitement.";
      try {
        const d = e.response?.data;
        if (d instanceof ArrayBuffer) {
          const text = new TextDecoder().decode(d);
          try { msg = JSON.parse(text).error || text.slice(0, 200); } catch { msg = text.slice(0, 200) || msg; }
        } else if (d?.error) { msg = d.error; }
      } catch {}
      setErr(msg);
    }
    setSaving(false);
  };

  return (
    <div className="modal fade show" style={{display:"block", background:"rgba(0,0,0,.5)", zIndex:1055}}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header"><h5><i className="bi bi-pen me-2"/>Traiter le document</h5><button className="btn-close" onClick={onClose}/></div>
          <div className="modal-body">
            {err && <div className="alert alert-danger py-2">{err}</div>}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="fw-semibold">{doc.titre}</div>
              <div className="text-muted small">De : {doc.deposant} · {doc.depose_le}</div>
              {doc.message && <div className="fst-italic small mt-1">"{doc.message}"</div>}
            </div>
            {doc.pdf_original_url && (
              <div className="mb-3">
                <DownloadLink url={doc.pdf_original_url} filename={`document_${doc.reference || doc.id}.pdf`} mimeType="application/pdf" downloadOnWeb={false} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm">
                  <i className="bi bi-eye me-1"/>Consulter le document original
                </DownloadLink>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label fw-semibold">Votre décision *</label>
              <div className="d-flex gap-3">
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="action" id="act_signer" value="signer" checked={action==="signer"} onChange={()=>setAction("signer")}/>
                  <label className="form-check-label text-success fw-semibold" htmlFor="act_signer"><i className="bi bi-check-circle me-1"/>Signer</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="radio" name="action" id="act_refuser" value="refuser" checked={action==="refuser"} onChange={()=>setAction("refuser")}/>
                  <label className="form-check-label text-danger fw-semibold" htmlFor="act_refuser"><i className="bi bi-x-circle me-1"/>Refuser</label>
                </div>
              </div>
            </div>
            {action === "refuser" && (
              <div className="mb-2">
                <label className="form-label">Motif du refus (optionnel)</label>
                <textarea className="form-control" rows={2} value={motif} onChange={e=>setMotif(e.target.value)} placeholder="Expliquez pourquoi vous refusez…"/>
              </div>
            )}
            {action === "signer" && (
              <div className="alert alert-info py-2 small"><i className="bi bi-info-circle me-1"/>Votre signature sera apposée sur le document. Le déposant sera notifié et pourra télécharger le PDF signé.</div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button className={`btn ${action==="signer"?"btn-success":"btn-danger"}`} onClick={submit} disabled={saving || !action}>
              {saving ? <span className="spinner-border spinner-border-sm me-1"/> : null}
              {action === "signer" ? "✍ Signer et télécharger" : action === "refuser" ? "❌ Confirmer le refus" : "Confirmer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommuniqueModal({ onClose, onSuccess }) {
  const [membres, setMembres]   = React.useState([]);
  const [form, setForm]         = React.useState({ objet: "", corps: "" });
  const [destIds, setDestIds]   = React.useState([]);
  const [saving, setSaving]     = React.useState(false);
  const [err, setErr]           = React.useState("");

  React.useEffect(() => {
    api.get("signature/communique/").then(r => setMembres(r.data)).catch(() => {});
  }, []);

  const toggleDest = (id) => setDestIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.objet.trim()) return setErr("L'objet est requis.");
    if (!form.corps.trim()) return setErr("Le corps du communiqué est requis.");
    if (destIds.length === 0) return setErr("Sélectionnez au moins un destinataire.");
    setSaving(true); setErr("");
    try {
      const resp = await api.post("signature/communique/", {
        objet:         form.objet,
        corps:         form.corps,
        destinataires: destIds,
      }, { responseType: "arraybuffer" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      telechargerPuisProposerImpression(blob, `communique_${form.objet.replace(/\s+/g,"_").slice(0,30)}.pdf`, "le communiqué");
      onSuccess(form.objet);
    } catch(ex) {
      let msg = "Erreur lors de la génération.";
      try {
        const d = ex.response?.data;
        if (d instanceof ArrayBuffer) {
          const text = new TextDecoder().decode(d);
          try { msg = JSON.parse(text).error || text.slice(0, 300); }
          catch { msg = text.slice(0, 300) || msg; }
        } else if (d?.error) {
          msg = d.error;
        } else if (ex.message) {
          msg = ex.message;
        }
      } catch {}
      setErr(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade show" style={{display:"block", background:"rgba(0,0,0,.5)", zIndex:1055}}>
      <div className="modal-dialog modal-lg modal-dialog-centered" style={{maxHeight:"95vh"}}>
        <div className="modal-content" style={{maxHeight:"95vh", display:"flex", flexDirection:"column"}}>
          <div className="modal-header bg-success text-white flex-shrink-0">
            <h5 className="mb-0"><i className="bi bi-file-text me-2"/>Rédiger un communiqué officiel</h5>
            <button className="btn-close btn-close-white" onClick={onClose}/>
          </div>
          <form onSubmit={submit} style={{display:"flex", flexDirection:"column", flex:1, minHeight:0}}>
            <div className="modal-body" style={{overflowY:"auto", flex:1}}>
              {err && <div className="alert alert-danger py-2">{err}</div>}
              <div className="alert alert-info py-2 small mb-3">
                <i className="bi bi-info-circle me-1"/>Le communiqué sera généré en PDF avec le logo Quick Learning et votre signature numérique, puis envoyé aux destinataires sélectionnés.
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Destinataire(s) *</label>
                <div className="border rounded p-2" style={{maxHeight:140, overflowY:"auto"}}>
                  {membres.length === 0
                    ? <span className="text-muted small">Aucun membre disponible.</span>
                    : membres.map(m => (
                      <div key={m.id} className="form-check">
                        <input className="form-check-input" type="checkbox" id={`d_${m.id}`}
                          checked={destIds.includes(m.id)} onChange={() => toggleDest(m.id)}/>
                        <label className="form-check-label" htmlFor={`d_${m.id}`}>
                          <strong>{m.nom}</strong> <span className="text-muted small">— {m.role}</span>
                        </label>
                      </div>
                    ))
                  }
                </div>
                <div className="form-text">{destIds.length} destinataire(s) sélectionné(s)</div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Objet *</label>
                <input className="form-control" placeholder="Ex : Réunion pédagogique du 30 juin 2026"
                  value={form.objet} onChange={e => setForm(f=>({...f, objet: e.target.value}))} required maxLength={200}/>
              </div>

              <div className="mb-2">
                <label className="form-label fw-semibold">Corps du communiqué *</label>
                <textarea className="form-control" rows={8}
                  style={{fontFamily:"Georgia, serif", fontSize:14, lineHeight:1.7}}
                  placeholder={"Madame, Monsieur,\n\nNous vous informons que...\n\nEn vous souhaitant bonne réception,"}
                  value={form.corps} onChange={e => setForm(f=>({...f, corps: e.target.value}))} required/>
                <div className="form-text">Chaque paragraphe séparé par une ligne vide sera mis en forme automatiquement.</div>
              </div>
            </div>
            <div className="modal-footer flex-shrink-0 flex-column align-items-stretch gap-2">
              {err && <div className="alert alert-danger py-2 mb-0 w-100">{err}</div>}
              <div className="d-flex justify-content-end gap-2 w-100">
                <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
                <button type="submit" className="btn btn-success" disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-1"/>Génération en cours…</>
                    : <><i className="bi bi-send-check me-1"/>Signer, envoyer et télécharger</>
                  }
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminPanel({ user, onLogout, onUserUpdate }) {
  const isPureComptable = !!user?.is_comptable && !user?.is_superuser && !user?.is_staff;
  const isPureCM = !!user?.is_community_manager && !user?.is_superuser && !user?.is_staff;
  const isPureSec = !!user?.is_secretaire && !user?.is_superuser && !user?.is_staff;
  const isPureAmb = !!user?.is_brand_ambassador && !user?.is_superuser && !user?.is_staff;
  const [page, setPage]         = useState(isPureAmb ? "amb_dashboard" : isPureSec ? "sec_dashboard" : isPureCM ? "cm_dashboard" : isPureComptable ? "compta_dashboard" : "dashboard");
  // Pastilles rouges (messages non lus, nouvelles assistances) : rechargées à chaque changement de page
  const compteurs = useCompteurs(page);
  useNotificationsDeLaRubrique(RUBRIQUES_ADMIN[page], compteurs.notifications);
  const [collapsed, setCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [auditUser, setAuditUser] = useState("");
  const [stats, setStats]       = useState(null);
  const [banner, setBanner]               = useState(null);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const [localUser, setLocalUser] = useState(user);

  const isSuperAdmin    = !!localUser?.is_superuser;
  const [visite, setVisite] = useState(() => !visiteDejaVue("admin", user));
  const isPrimaryAdmin  = !!localUser?.is_primary_admin;
  const accesCM         = isPrimaryAdmin || !!localUser?.is_community_manager;
  const accesSec        = isPrimaryAdmin || !!localUser?.is_secretaire;
  const accesAmb        = !!localUser?.is_brand_ambassador;
  const gereAmbassadeurs = isSuperAdmin || !!localUser?.is_comptable || !!localUser?.is_secretaire || !!localUser?.is_community_manager;

  function handleProfileSave(updated) {
    setLocalUser(u => ({ ...u, ...updated }));
    if (onUserUpdate) onUserUpdate(prev => ({ ...prev, ...updated }));
  }

  const dismissBanner = () => setBannerVisible(false);

  useEffect(() => {
    api.get("admin/stats/").then(r => setStats(r.data)).catch(() => {});
    api.get("banner/").then(r => {
      setBanner(r.data || null);
      setBannerVisible(true);
    }).catch(() => {});
  }, []);

  // Navigation inter-composants via événement custom
  useEffect(() => {
    const handler = e => setPage(e.detail);
    window.addEventListener("ql-open-page", handler);
    return () => window.removeEventListener("ql-open-page", handler);
  }, []);

  // Ping toutes les minutes pour tracker l'activité et la dernière déconnexion
  useEffect(() => {
    api.post("ping/").catch(() => {});
    const interval = setInterval(() => { api.post("ping/").catch(() => {}); }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Verrou scroll body quand sidebar mobile ouverte
  useEffect(() => {
    if (sidebarOpen) { document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; }
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const handleToggle = () => {
    if (window.innerWidth <= 768) { setSidebarOpen(s => !s); }
    else { setCollapsed(c => !c); }
  };

  const handlePage = (p) => {
    setPage(p);
    setSidebarOpen(false);
  };

  const handleSearch = (q) => {
    setGlobalSearch(q);
    // Le secrétariat n'a pas accès à la gestion des utilisateurs : la recherche ouvre la liste des élèves.
    if (q.trim()) { setPage(isPureAmb ? "amb_ventes" : isPureSec ? "sec_eleves" : "users"); setSidebarOpen(false); }
  };

  const renderPage = () => {
    switch (page) {
      case "dashboard":  return <DashboardPage stats={stats} onPage={handlePage} />;
      case "supervision": return isSuperAdmin ? <SupervisionDashboard /> : <AccessDeniedPage />;
      case "diagnostic_email": return isSuperAdmin ? <DiagnosticEmailPage /> : <AccessDeniedPage />;
      case "users":      return <UsersPage key="users"       globalSearch={globalSearch} isSuperAdmin={isSuperAdmin} onAuditUser={u => { setAuditUser(u); setPage("audit"); }} />;
      case "enseignants":return <UsersPage key="enseignants" globalSearch="" roleFilter="enseignant" isSuperAdmin={isSuperAdmin} />;
      case "activite":   return <ActivitePage isSuperAdmin={isSuperAdmin} />;
      case "manuels":    return <ManuelsPage />;
      case "mes_contenus": return (localUser?.is_staff || localUser?.is_superuser) ? <MesContenus /> : <AccessDeniedPage />;
      case "modules":    return <ContentListPage type="modules" />;
      case "lecons":     return <ContentListPage type="lecons" />;
      case "videos":     return <ContentListPage type="videos" />;
      case "qcm":        return <ContentListPage type="qcm" />;
      case "exercices":  return <ContentListPage type="exercices" />;
      case "groupes":    return <GroupesPage />;
      case "soutien_scolaire": return isSuperAdmin ? <AdminSupportSpace user={localUser} /> : <AccessDeniedPage />;
      case "referrals": return isSuperAdmin ? <AdminReferralSpace /> : <AccessDeniedPage />;
      case "classement_eleves": return isSuperAdmin ? <StudentRankingAdmin /> : <AccessDeniedPage />;
      case "codes":           return isSuperAdmin ? <CodesPage /> : <AccessDeniedPage />;
      case "codes_comptable": return isPrimaryAdmin ? <CodesComptablePage key="comptable" /> : <AccessDeniedPage />;
      case "codes_community": return isPrimaryAdmin ? <CodesComptablePage key="community" type="community" /> : <AccessDeniedPage />;
      case "prix_manuels":    return isSuperAdmin ? <PrixManuelsPage /> : <AccessDeniedPage />;
      case "audit":           return isPrimaryAdmin ? <AuditLogPage key={auditUser || "all"} initialUser={auditUser} /> : <AccessDeniedPage />;
      case "compta_dashboard":return (isPrimaryAdmin || localUser?.is_comptable) ? <ComptaDashboardPage /> : <AccessDeniedPage />;
      case "compta_revenus":  return (isPrimaryAdmin || localUser?.is_comptable) ? <ComptaRevenusPage /> : <AccessDeniedPage />;
      case "compta_salaires": return (isPrimaryAdmin || localUser?.is_comptable) ? <ComptaSalairesPage /> : <AccessDeniedPage />;
      case "compta_depenses": return (isPrimaryAdmin || localUser?.is_comptable) ? <ComptaDepensesPage /> : <AccessDeniedPage />;
      case "cm_dashboard":    return accesCM ? <CMDashboardPage user={localUser} /> : <AccessDeniedPage />;
      case "cm_calendrier":   return accesCM ? <CMCalendrierPage /> : <AccessDeniedPage />;
      case "cm_contenus":     return accesCM ? <CMContenusPage /> : <AccessDeniedPage />;
      case "cm_communaute":   return accesCM ? <CMCommunautePage /> : <AccessDeniedPage />;
      case "cm_statistiques": return accesCM ? <CMStatistiquesPage /> : <AccessDeniedPage />;
      case "cm_ressources":   return accesCM ? <CMRessourcesPage /> : <AccessDeniedPage />;
      case "codes_secretariat": return isPrimaryAdmin ? <CodesComptablePage key="secretariat" type="secretariat" /> : <AccessDeniedPage />;
      case "codes_ambassadeur": return isSuperAdmin ? <CodesComptablePage key="ambassadeur" type="ambassadeur" /> : <AccessDeniedPage />;
      case "ambassadeurs":    return gereAmbassadeurs ? <GestionAmbassadeursPage /> : <AccessDeniedPage />;
      case "amb_dashboard":   return accesAmb ? <AMBDashboardPage user={localUser} /> : <AccessDeniedPage />;
      case "amb_vente":       return accesAmb ? <AMBNouvelleVentePage /> : <AccessDeniedPage />;
      case "amb_ventes":      return accesAmb ? <AMBVentesPage /> : <AccessDeniedPage />;
      case "amb_versements":  return accesAmb ? <AMBVersementsPage /> : <AccessDeniedPage />;
      case "amb_stock":       return accesAmb ? <AMBStockPage /> : <AccessDeniedPage />;
      case "amb_recouvrements": return accesAmb ? <AMBRecouvrementsPage /> : <AccessDeniedPage />;
      case "amb_reversements": return accesAmb ? <AMBReversementsPage /> : <AccessDeniedPage />;
      case "amb_performances": return accesAmb ? <AMBPerformancesPage /> : <AccessDeniedPage />;
      case "sec_dashboard":   return accesSec ? <SECDashboardPage user={localUser} nonLus={compteurs.messages} /> : <AccessDeniedPage />;
      case "sec_demandes":    return accesSec ? <CMCommunautePage /> : <AccessDeniedPage />;
      case "sec_agenda":      return accesSec ? <SECRegistrePage key="rdv" type="rendez_vous" /> : <AccessDeniedPage />;
      case "sec_taches":      return accesSec ? <SECRegistrePage key="taches" type="tache" /> : <AccessDeniedPage />;
      case "sec_parents":     return accesSec ? <SECAnnuairePage key="parents" categorie="parents" /> : <AccessDeniedPage />;
      case "sec_enseignants": return accesSec ? <SECAnnuairePage key="enseignants" categorie="enseignants" /> : <AccessDeniedPage />;
      case "sec_eleves":      return accesSec ? <SECAnnuairePage key="eleves" categorie="eleves" rechercheInitiale={isPureSec ? globalSearch : ""} /> : <AccessDeniedPage />;
      case "sec_etablissements": return accesSec ? <SECRegistrePage key="etab" type="etablissement" /> : <AccessDeniedPage />;
      case "sec_manuels":     return accesSec ? <SECManuelsPage /> : <AccessDeniedPage />;
      case "sec_courriers":   return accesSec ? <SECRegistrePage key="courriers" type="courrier" /> : <AccessDeniedPage />;
      case "sec_documents":   return accesSec ? <SECRegistrePage key="documents" type="document" statutInitial={isPureSec ? null : "a_valider"} /> : <AccessDeniedPage />;
      case "sec_compte":      return accesSec ? <SECComptePage user={localUser} onProfil={() => setShowProfile(true)} /> : <AccessDeniedPage />;
      case "rapports":        return isSuperAdmin ? <RapportsPage /> : (localUser?.is_staff ? <RapportEnseignantPage user={localUser} /> : <AccessDeniedPage />);
      case "formules":    return <FormulesAdminPage />;
      case "devoirs":     return <EnseignantDevoirs />;
      case "lives_admin": return (localUser?.is_superuser || localUser?.is_staff) ? <LivesAdminPage /> : <AccessDeniedPage />;
      // Messages : échanges internes (équipe, parents, générateurs). Les élèves passent par l'Assistance.
      case "messages":       return <MessagesView user={localUser} compact perimetre={(localUser?.is_staff || localUser?.is_superuser) ? "interne" : "tout"} />;
      case "assistances":    return (localUser?.is_staff || localUser?.is_superuser)
        ? <AssistanceEnseignant user={localUser} exercices={<AssistancesPage user={localUser} />} /> : <AccessDeniedPage />;
      case "ma_signature":   return <MaSignaturePage />;
      case "boite_signature":return <BoiteSignaturePage user={localUser} />;
      default:           return <PlaceholderPage title={NAV.flatMap(s => s.items).find(i => i.page === page)?.label || page} />;
    }
  };

  const showBanner = !!banner && bannerVisible;

  return (
    <div className={`ap-root${collapsed ? " ap-collapsed" : ""}`}>
      {sidebarOpen && <div className="ap-sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      {visite && <VisiteGuidee etapes={ETAPES_ADMIN} espace="admin" user={localUser} onClose={() => setVisite(false)} />}
      <Sidebar page={page} onPage={handlePage} collapsed={collapsed} isOpen={sidebarOpen} user={localUser} onProfileClick={() => setShowProfile(true)} compteurs={compteurs} />
      <div className="ap-main">
        <TopBar
          nonLus={compteurs.messages + compteurs.assistances + (compteurs.boite || 0)}
          onAide={() => setVisite(true)}
          onToggle={handleToggle}
          onSearch={handleSearch}
          searchQ={globalSearch}
          user={localUser}
          onLogout={onLogout}
          onProfileClick={() => setShowProfile(true)}
        />
        {showBanner && (banner.media_type || banner.youtube_id) && (
          <BannierePublicitaire banner={banner} onClose={dismissBanner} />
        )}
        {showBanner && !banner.media_type && !banner.youtube_id && (
          <div className={`ql-banner ql-banner-${banner.msg_type}`}>
            <div className="ql-banner-body">
              <i className={`bi ${
                banner.msg_type === "success" ? "bi-check-circle-fill" :
                banner.msg_type === "warning" ? "bi-exclamation-triangle-fill" :
                banner.msg_type === "danger"  ? "bi-x-octagon-fill" :
                "bi-info-circle-fill"
              } ql-banner-icon`}></i>
              <div className="ql-banner-text">
                {banner.title && <strong className="ql-banner-title">{banner.title}</strong>}
                <span className="ql-banner-content"><MathText text={banner.content} /></span>
              </div>
            </div>
            <button className="ql-banner-close" onClick={dismissBanner} aria-label="Fermer">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        )}
        <div className={`ap-content${page === "messages" || page === "assistances" ? " ap-content--fullscreen" : ""}`}>
          {renderPage()}
        </div>
      </div>

      {showProfile && (
        <ProfileModal
          user={localUser}
          onClose={() => setShowProfile(false)}
          onSave={handleProfileSave}
        />
      )}
    </div>
  );
}
